import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../../../layouts/AppLayout/AppLayout';
import { NioSection, NioButton } from '../../../components';
import { useTranslation } from '../../../i18n/I18nProvider';
import { translateWizardLabel } from '../../../i18n/translateWizard';
import { formatFileSize } from '../../../utils/formatFileSize';

const MAX_FILES = 4;
const MAX_FILE_SIZE_BYTES = 7 * 1024 * 1024;

function RequestDemandStep4() {
  const navigate = useNavigate();
  const location = useLocation();
  const { serviceId } = useParams();
  const { t } = useTranslation();
  const tw = (text) => translateWizardLabel(t, text);

  const selectedSubService = location.state?.selectedSubService || t('common.subService');
  const parentServiceTitle = location.state?.parentServiceTitle || t('common.service');
  const projectTitle = location.state?.projectTitle || '';
  const requestDetails = location.state?.requestDetails || {};
  const step3FirstChoice = String(requestDetails?.firstChoices?.[0] || '').toLowerCase();
  const serviceNeedle = `${selectedSubService} ${parentServiceTitle}`.toLowerCase();
  const serviceNeedleAscii = serviceNeedle.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const subNormAscii = String(selectedSubService || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  const parentNormAscii = String(parentServiceTitle || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  const isDebarrasMenageNettoyageService =
    subNormAscii.includes('debarass') ||
    (subNormAscii.includes('debarras') && parentNormAscii.includes('nettoyage'));
  const isDebarrasService =
    subNormAscii.includes('debarras') && !isDebarrasMenageNettoyageService;
  const isMontageDemontageMeublesService =
    serviceNeedleAscii.includes('montage') &&
    serviceNeedleAscii.includes('demont') &&
    (serviceNeedleAscii.includes('meuble') || serviceNeedleAscii.includes('meubles'));
  const isAccompagnementPersonnesAgeesService =
    serviceNeedleAscii.includes('accompagn') &&
    serviceNeedleAscii.includes('personne') &&
    serviceNeedleAscii.includes('agee');
  const isGardeStockageMeublesService =
    serviceNeedleAscii.includes('garde') &&
    serviceNeedleAscii.includes('stockage') &&
    (serviceNeedleAscii.includes('meuble') || serviceNeedleAscii.includes('meubles'));
  const isGardeAccompagnementEnfantsService =
    serviceNeedleAscii.includes('garde') &&
    serviceNeedleAscii.includes('accompagn') &&
    serviceNeedleAscii.includes('enfant');
  const isLocationUtilitaireAvecChauffeurService =
    serviceNeedleAscii.includes('location') &&
    serviceNeedleAscii.includes('utilitaire') &&
    serviceNeedleAscii.includes('chauffeur');
  const isMenageSurfaceMeublesService =
    serviceNeedleAscii.includes('menage') &&
    serviceNeedleAscii.includes('surface') &&
    (serviceNeedleAscii.includes('meuble') || serviceNeedleAscii.includes('meubles'));
  const isRepassageService = serviceNeedleAscii.includes('repassage');
  const isNettoyageVitresService =
    serviceNeedleAscii.includes('nettoyage') && serviceNeedleAscii.includes('vitre');
  const isNettoyageTapisCanapesService =
    serviceNeedleAscii.includes('nettoyage') &&
    serviceNeedleAscii.includes('tapis') &&
    serviceNeedleAscii.includes('canape');
  const isOrganisationRangementService =
    subNormAscii.includes('organisation') &&
    subNormAscii.includes('rangement') &&
    !subNormAscii.includes('meuble') &&
    !subNormAscii.includes('meubles');
  const isOuverturePorteService =
    subNormAscii.includes('ouverture') && subNormAscii.includes('porte');
  const isPortesSecuriseesService =
    subNormAscii.includes('porte') &&
    subNormAscii.includes('securis') &&
    !subNormAscii.includes('ouverture');
  const isSerrurerieService = subNormAscii.includes('serrurerie');
  const isEntretienReparationMecaniqueService =
    subNormAscii.includes('entretien') &&
    subNormAscii.includes('reparation') &&
    !subNormAscii.includes('meuble') &&
    !subNormAscii.includes('depannage');
  const isChangementRouesMecaniqueService =
    subNormAscii.includes('changement') && subNormAscii.includes('roue');
  const isBatterieElectriqueMecaniqueService =
    subNormAscii.includes('batterie') && subNormAscii.includes('elect');
  const isPlaquetteFreinMecaniqueService =
    subNormAscii.includes('plaquette') && subNormAscii.includes('frein');
  const isDepannageRemorquageMecaniqueService =
    (subNormAscii.includes('depannage') || subNormAscii.includes('depan')) &&
    (subNormAscii.includes('remorquage') || subNormAscii.includes('remorqu'));
  const isChangementPiecesAutoMecaniqueService =
    subNormAscii.includes('changement') &&
    subNormAscii.includes('piece') &&
    subNormAscii.includes('auto');
  const isDiagnosticNettoyageMecaniqueService =
    (subNormAscii.includes('diagnost') || subNormAscii.includes('diagnostique')) &&
    subNormAscii.includes('nettoy');
  const isOutdoorPavingService =
    serviceNeedle.includes('dalle exterieur') ||
    serviceNeedle.includes('dalle extérieur') ||
    serviceNeedle.includes('dalles exterieur') ||
    serviceNeedle.includes('dalles extérieur') ||
    serviceNeedle.includes('pavage exterieur') ||
    serviceNeedle.includes('pavage extérieur');
  const isWallpaperService =
    serviceNeedle.includes('papier peint') ||
    serviceNeedle.includes('papier-peint') ||
    serviceNeedle.includes('revetement mural') ||
    serviceNeedle.includes('revêtement mural');
  const isPlasterboardService =
    serviceNeedle.includes('plaque de platre') ||
    serviceNeedle.includes('plaque de plâtre') ||
    serviceNeedle.includes('plaques de platre') ||
    serviceNeedle.includes('plaques de plâtre') ||
    serviceNeedle.includes('plaque platre') ||
    serviceNeedle.includes('plaque plâtre') ||
    serviceNeedle.includes('plaques platre') ||
    serviceNeedle.includes('plaques plâtre') ||
    serviceNeedle.includes('placo');
  const isPlasterService =
    serviceNeedle.includes('platrage') ||
    serviceNeedle.includes('plâtrage') ||
    serviceNeedle.includes('platrerie') ||
    serviceNeedle.includes('plâtrerie');
  const isParquetService = serviceNeedle.includes('parquet');
  const isTerraceService =
    serviceNeedle.includes('terrasse') ||
    serviceNeedle.includes('terasse') ||
    serviceNeedle.includes('deck');
  const isFloorCoverService =
    serviceNeedle.includes('revetement de sol') ||
    serviceNeedle.includes('revêtement de sol');
  const isMasonryService =
    serviceNeedle.includes('maconnerie') ||
    serviceNeedle.includes('maçonnerie');
  const usesDocumentStepAtStep4 = isFloorCoverService || isMasonryService;
  const isPolishedConcreteService =
    serviceNeedle.includes('beton poli') ||
    serviceNeedle.includes('béton poli');
  const isCarpetService = serviceNeedle.includes('tapis');
  const isSkirtingService =
    serviceNeedle.includes('plinthe') ||
    serviceNeedle.includes('plinthes');
  const isLightingHomeStagingService =
    serviceNeedle.includes('eclairage et home staging') ||
    serviceNeedle.includes('éclairage et home staging') ||
    serviceNeedle.includes('eclairage home staging') ||
    serviceNeedle.includes('éclairage home staging');
  const isCustomFurnitureService =
    serviceNeedle.includes('meuble sur mesure') ||
    serviceNeedle.includes('meubles sur mesure') ||
    serviceNeedle.includes('sur mesure');
  const isDecorationConsultingService =
    serviceNeedle.includes('conseil en decoration') ||
    serviceNeedle.includes('conseil en décoration') ||
    serviceNeedle.includes('conseils en decoration') ||
    serviceNeedle.includes('conseils en décoration');
  const isDecorationSpacePlanningService =
    serviceNeedle.includes('decoration et amenagement des espaces') ||
    serviceNeedle.includes('décoration et aménagement des espaces') ||
    serviceNeedle.includes('amenagement des espaces') ||
    serviceNeedle.includes('aménagement des espaces');
  const isWallDecorationAccessoriesService =
    serviceNeedle.includes('decoration murale et accessoires') ||
    serviceNeedle.includes('décoration murale et accessoires') ||
    serviceNeedle.includes('decoration murale') ||
    serviceNeedle.includes('décoration murale');
  const isFurnitureRepairService =
    serviceNeedle.includes('reparation des meubles') ||
    serviceNeedle.includes('réparation de meubles') ||
    serviceNeedle.includes('reparation meuble') ||
    serviceNeedle.includes('réparation meuble');
  const isFurnitureTreatmentService =
    serviceNeedle.includes('entretien et traitement des meubles') ||
    serviceNeedle.includes('entretien traitement des meubles') ||
    serviceNeedle.includes('traitement des meubles') ||
    serviceNeedle.includes('traitement meuble') ||
    serviceNeedle.includes('traitement de meubles');
  const isKitchenFurnitureService =
    serviceNeedle.includes('meubles de cuisine') ||
    serviceNeedle.includes('meuble de cuisine');
  const isHomeApplianceService =
    serviceNeedle.includes('appareils menagers') ||
    serviceNeedle.includes('appareils ménagers') ||
    serviceNeedle.includes('appareil menager') ||
    serviceNeedle.includes('appareil ménager');
  const isKitchenHoodService =
    serviceNeedle.includes('hotte de cuisine') ||
    serviceNeedle.includes('hotte cuisine') ||
    serviceNeedle.includes('hottes de cuisine');
  const isVmcService = serviceNeedleAscii.includes('vmc');
  const isChargementDechargementService =
    serviceNeedleAscii.includes('chargement') && serviceNeedleAscii.includes('decharg');
  const isShowerCabinService =
    serviceNeedle.includes('cabine de douche') ||
    serviceNeedle.includes('cabine douche') ||
    serviceNeedle.includes('douche cabine');
  const isBathroomLightingService =
    (serviceNeedle.includes('eclairage') || serviceNeedle.includes('éclairage')) &&
    serviceNeedle.includes('salle de bain');
  const isBornesTableauxElectriquesService =
    serviceNeedleAscii.includes('borne') &&
    serviceNeedleAscii.includes('tableau') &&
    serviceNeedleAscii.includes('electri');
  const isEclairageService =
    (serviceNeedle.includes('eclairage') || serviceNeedle.includes('éclairage')) &&
    !isBornesTableauxElectriquesService &&
    !isLightingHomeStagingService &&
    !isBathroomLightingService;
  const isPrisesInterrupteurService =
    serviceNeedle.includes('prise') ||
    serviceNeedle.includes('prises') ||
    serviceNeedle.includes('interrupteur') ||
    serviceNeedle.includes('interruptteur');
  const isDomotiqueService = serviceNeedle.includes('domotique');
  const isInterphoneService = serviceNeedle.includes('interphone');
  const isAlarmesSecuriteService =
    subNormAscii.includes('alarme') && !subNormAscii.includes('camera');
  const isCamerasSecuriteService =
    subNormAscii.includes('camera') && !subNormAscii.includes('alarme');
  const isAlarmeCameraService =
    !isAlarmesSecuriteService &&
    !isCamerasSecuriteService &&
    (serviceNeedle.includes('alarme') ||
      serviceNeedle.includes('camera') ||
      serviceNeedle.includes('caméra'));
  const isToiletService =
    serviceNeedle.includes('toilette') ||
    serviceNeedle.includes('toilettes') ||
    serviceNeedle.includes('toilatette') ||
    serviceNeedle.includes('wc');
  const isBathroomStorageService =
    serviceNeedle.includes('meubles et rangement') ||
    serviceNeedle.includes('meuble et rangement') ||
    (serviceNeedle.includes('meubles') && serviceNeedle.includes('rangement') && serviceNeedle.includes('salle de bain'));
  const isWaterHeaterService =
    serviceNeedle.includes('chauffe eau') ||
    serviceNeedle.includes('chauffe-eau');
  const isMasticService = serviceNeedle.includes('mastic');
  const isClimatisationSubTitle =
    String(selectedSubService || '').trim().toLowerCase() === 'climatisation';
  const isClimatisationService =
    isClimatisationSubTitle || serviceNeedle.includes('climatisation');
  const chauffageSubNorm = String(selectedSubService || '').trim().toLowerCase();
  const isChauffageService =
    chauffageSubNorm.includes('chauffage') &&
    !chauffageSubNorm.includes('chauffe-eau') &&
    !chauffageSubNorm.includes('chauffe eau');
  const chaudiereSubNorm = String(selectedSubService || '').trim().toLowerCase();
  const isChaudiereService = chaudiereSubNorm.includes('chaudi');
  const tuyauxSubNorm = String(selectedSubService || '').trim().toLowerCase();
  const isTuyauxService = tuyauxSubNorm.includes('tuyaux') || tuyauxSubNorm === 'tuyau';
  const isEntretienDepannagePlumbingService =
    (tuyauxSubNorm.includes('entretien') &&
      (tuyauxSubNorm.includes('depannage') || tuyauxSubNorm.includes('dépannage'))) ||
    tuyauxSubNorm.includes('entretien et depannage') ||
    tuyauxSubNorm.includes('entretien et dépannage');
  const isPlomberieSubTitle =
    String(selectedSubService || '').trim().toLowerCase() === 'plomberie';
  const isKitchenPlumbingService =
    isPlomberieSubTitle ||
    (serviceNeedle.includes('plomberie') &&
      (serviceNeedle.includes('cuisine') ||
        serviceNeedle.includes('évier') ||
        serviceNeedle.includes('evier')));
  const isKitchenFaucetService =
    serviceNeedle.includes('robinetterie') &&
    (serviceNeedle.includes('cuisine') || serviceNeedle.includes('robinet'));
  const isKitchenStorageService =
    serviceNeedle.includes('espace rangement') ||
    serviceNeedle.includes('espace de rangement') ||
    serviceNeedle.includes('rangements cuisine') ||
    serviceNeedle.includes('rangement cuisine');
  const isBathroomService =
    serviceNeedle.includes('salle de bain') ||
    serviceNeedle.includes("salle d'eau") ||
    serviceNeedle.includes('salle de bains');
  const storesSubNorm = String(selectedSubService || '').trim().toLowerCase();
  const storesSubNormAscii = storesSubNorm.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const fenetresSubNormAscii = String(selectedSubService || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  const isFenetresPortesFenetresService =
    fenetresSubNormAscii.includes('fenetres') && fenetresSubNormAscii.includes('portes-fenetres');
  const isStoresMenuiserieService = storesSubNorm === 'stores';
  const isStoreMotoriseService =
    storesSubNormAscii.includes('store motorise') ||
    storesSubNormAscii.includes('stores motorise');
  const isPortailMotoriseService = storesSubNormAscii.includes('portail motorise');
  const isVoletMotoriseService = storesSubNormAscii.includes('volet motorise');
  const isGarageMotoriseService = storesSubNormAscii.includes('garage motorise');
  const isPortailMenuiserieService = storesSubNorm === 'portail';
  const isPorteGarageMenuiserieService =
    storesSubNorm.includes('porte de garage') || storesSubNorm.includes('portes de garage');
  const isPotagerService = storesSubNormAscii.includes('potager');
  const isFleursService = storesSubNormAscii.includes('fleur');
  const isFeuillesService = storesSubNormAscii.includes('feuille');
  const isArbustesService =
    storesSubNormAscii.includes('arbust') ||
    step3FirstChoice.includes('arbuste');
  const isClairiereService = storesSubNormAscii.includes('clairiere');
  const isArbrePlantationService =
    (storesSubNorm.includes('arbre') &&
      !storesSubNorm.includes('abattage') &&
      !storesSubNorm.includes('abbatage')) ||
    step3FirstChoice.includes('plantez un arbre') ||
    step3FirstChoice.includes('transplanter');
  const isAbattageArbresService =
    (storesSubNorm.includes('abattage') || storesSubNorm.includes('abbatage')) &&
    storesSubNorm.includes('arbre');
  const isPelouseService = storesSubNorm.includes('pelouse');
  const isEspaceVertsService =
    storesSubNorm.includes('espace vert') ||
    storesSubNorm.includes('espaces verts') ||
    storesSubNorm.includes('espace verts');
  const isHaiesService =
    storesSubNorm.includes('haie') ||
    storesSubNorm.includes('haies') ||
    step3FirstChoice.includes('haie');
  const isTondreService =
    storesSubNorm.includes('tondre') ||
    step3FirstChoice.includes('tondez la pelouse') ||
    step3FirstChoice.includes('tondre');
  const isPorteMenuiserieService = storesSubNorm === 'porte';
  const isVestiaireMenuiserieService = storesSubNorm === 'vestiaire';
  const isObturateurMenuiserieService = storesSubNorm.includes('obturateur');
  const isCuisineMenuiserieService =
    storesSubNorm === 'cuisine' && serviceNeedle.toLowerCase().includes('menuiserie');
  const isCurtainRodService =
    serviceNeedle.includes('tringle a rideaux') ||
    serviceNeedle.includes('tringle à rideaux') ||
    serviceNeedle.includes('tringles a rideaux') ||
    serviceNeedle.includes('tringles à rideaux');
  const isShelfService =
    serviceNeedle.includes('etagere') ||
    serviceNeedle.includes('étagère') ||
    serviceNeedle.includes('étageres') ||
    serviceNeedle.includes('étagères');
  const isFurnitureAssemblyService =
    !isMontageDemontageMeublesService &&
    (serviceNeedle.includes('assemblee') ||
      serviceNeedle.includes('assemblée') ||
      serviceNeedle.includes('assemblage') ||
      serviceNeedle.includes('assembler') ||
      serviceNeedle.includes('montage meuble') ||
      serviceNeedle.includes('montage des meubles') ||
      step3FirstChoice.includes('assembler') ||
      step3FirstChoice.includes('démonter') ||
      step3FirstChoice.includes('demonter'));
  const isFixationMuralService =
    serviceNeedle.includes('fixation murale') ||
    serviceNeedle.includes('fixationmurale');
  const isAssemblyService = isFurnitureAssemblyService;
  const isPaintingService =
    serviceNeedle.includes('peinture') ||
    serviceNeedle.includes('peindre') ||
    serviceNeedle.includes('paint');

  const [bedroomCount, setBedroomCount] = useState('');
  const [installationType, setInstallationType] = useState('');
  const [obturateurCount, setObturateurCount] = useState('');
  const [obturateurType, setObturateurType] = useState('');
  const [obturateurMaterial, setObturateurMaterial] = useState('');
  const [obturateurMasonryWork, setObturateurMasonryWork] = useState('');
  const [voletMotoriseCount, setVoletMotoriseCount] = useState('');
  const [voletMotoriseType, setVoletMotoriseType] = useState('');
  const [voletMotoriseMaterial, setVoletMotoriseMaterial] = useState('');
  const [voletMotoriseMasonryWork, setVoletMotoriseMasonryWork] = useState('');
  const [parquetUnderlay, setParquetUnderlay] = useState('');
  const [parquetExistingFloorType, setParquetExistingFloorType] = useState('');
  const [floorCoverFormat, setFloorCoverFormat] = useState('');
  const [preparatoryWork, setPreparatoryWork] = useState('');
  const [coveredSurface, setCoveredSurface] = useState('');
  const [finishType, setFinishType] = useState('');
  const [wallPlasterSurface, setWallPlasterSurface] = useState('');
  const [ceilingPlasterSurface, setCeilingPlasterSurface] = useState('');
  const [wallLength, setWallLength] = useState('');
  const [wallHeight, setWallHeight] = useState('');
  const [wallCoverType, setWallCoverType] = useState('');
  const [wallCoverRemoval, setWallCoverRemoval] = useState('');
  const [paintSurfaceState, setPaintSurfaceState] = useState('');
  const [protectionAreas, setProtectionAreas] = useState('');
  const [terraceInstallationBase, setTerraceInstallationBase] = useState('');
  const [terracePreparationNeed, setTerracePreparationNeed] = useState('');
  const [polishedConcreteExistingFloor, setPolishedConcreteExistingFloor] = useState('');
  const [carpetInstallType, setCarpetInstallType] = useState('');
  const [carpetRoomEmpty, setCarpetRoomEmpty] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadError, setUploadError] = useState('');
  const [baseboardType, setBaseboardType] = useState('');
  const [baseboardFinish, setBaseboardFinish] = useState('');
  const [baseboardLength, setBaseboardLength] = useState('');
  const [lightingObjective, setLightingObjective] = useState('');
  const [lightingType, setLightingType] = useState('');
  const [eclairageWallType, setEclairageWallType] = useState('');
  const [eclairageElectricalModification, setEclairageElectricalModification] = useState('');
  const [bornesTableauxRooms, setBornesTableauxRooms] = useState([]);
  const [prisesInterrupteurCountRange, setPrisesInterrupteurCountRange] = useState('');
  const [prisesInterrupteurReady, setPrisesInterrupteurReady] = useState('');
  const [domotiqueDeviceCounts, setDomotiqueDeviceCounts] = useState({});
  const [domotiqueControlMode, setDomotiqueControlMode] = useState('');
  const [interphoneSystemType, setInterphoneSystemType] = useState('');
  const [interphoneInstallType, setInterphoneInstallType] = useState('');
  const [alarmeType, setAlarmeType] = useState('');
  const [alarmeElementCounts, setAlarmeElementCounts] = useState({});
  const [alarmesSecuriteLieu, setAlarmesSecuriteLieu] = useState('');
  const [alarmesSecuriteFonctionnalites, setAlarmesSecuriteFonctionnalites] = useState('');
  const [camerasSecuriteNombre, setCamerasSecuriteNombre] = useState('');
  const [customFurnitureMaterials, setCustomFurnitureMaterials] = useState([]);
  const [decorationConsultingStyle, setDecorationConsultingStyle] = useState('');
  const [decorationConsultingObjective, setDecorationConsultingObjective] = useState('');
  const [decorationConsultingSupportLevel, setDecorationConsultingSupportLevel] = useState('');
  const [spaceDecorationStyle, setSpaceDecorationStyle] = useState('');
  const [spaceDecorationObjective, setSpaceDecorationObjective] = useState('');
  const [wallDecorationElementType, setWallDecorationElementType] = useState('');
  const [wallDecorationStyle, setWallDecorationStyle] = useState('');
  const [furnitureRepairType, setFurnitureRepairType] = useState('');
  const [furnitureProblemNature, setFurnitureProblemNature] = useState('');
  const [furnitureRepairMaterial, setFurnitureRepairMaterial] = useState('');
  const [furnitureType, setFurnitureType] = useState('');
  const [furnitureMaterial, setFurnitureMaterial] = useState('');
  const [furnitureTreatmentType, setFurnitureTreatmentType] = useState('');
  const [kitchenFurnitureCounts, setKitchenFurnitureCounts] = useState({});
  const [kitchenWallFixRequired, setKitchenWallFixRequired] = useState('');
  const [applianceElectricalInstall, setApplianceElectricalInstall] = useState('');
  const [applianceWaterConnection, setApplianceWaterConnection] = useState('');
  const [kitchenHoodType, setKitchenHoodType] = useState('');
  const [kitchenHoodDuctNeed, setKitchenHoodDuctNeed] = useState('');
  const [vmcSystemType, setVmcSystemType] = useState('');
  const [vmcSystemLocation, setVmcSystemLocation] = useState('');
  const [chargementGoodsTypes, setChargementGoodsTypes] = useState([]);
  const [chargementAccessDifficulty, setChargementAccessDifficulty] = useState([]);
  const [chargementTruckDistance, setChargementTruckDistance] = useState('');
  const [montageDemontageMeublesCountRange, setMontageDemontageMeublesCountRange] = useState('');
  const [accompagnementAinesLieu, setAccompagnementAinesLieu] = useState('');
  const [accompagnementAinesAutonomie, setAccompagnementAinesAutonomie] = useState('');
  const [gardeStockageApproxVolume, setGardeStockageApproxVolume] = useState('');
  const [gardeStockageMainGoodsType, setGardeStockageMainGoodsType] = useState('');
  const [gardeStockageAdditionalServices, setGardeStockageAdditionalServices] = useState('');
  const [gardeEnfantsDureeSouhaitee, setGardeEnfantsDureeSouhaitee] = useState('');
  const [gardeEnfantsFrequence, setGardeEnfantsFrequence] = useState('');
  const [gardeEnfantsBesoinParticulier, setGardeEnfantsBesoinParticulier] = useState('');
  const [debarrasTypeLieu, setDebarrasTypeLieu] = useState('');
  const [debarrasAcces, setDebarrasAcces] = useState('');
  const [debarrasServicesSouhaites, setDebarrasServicesSouhaites] = useState('');
  const [debarrasMenageLieu, setDebarrasMenageLieu] = useState('');
  const [debarrasMenageAcces, setDebarrasMenageAcces] = useState('');
  const [debarrasMenageServicesVoulus, setDebarrasMenageServicesVoulus] = useState('');
  const [utilitaireChauffeurMode, setUtilitaireChauffeurMode] = useState('');
  const [utilitaireLieuPriseEnCharge, setUtilitaireLieuPriseEnCharge] = useState('');
  const [utilitaireVolumeTypeTransport, setUtilitaireVolumeTypeTransport] = useState('');
  const [menageSurfacesSuperficieApprox, setMenageSurfacesSuperficieApprox] = useState('');
  const [menageSurfacesPrestationType, setMenageSurfacesPrestationType] = useState('');
  const [menageSurfacesProduitsExigences, setMenageSurfacesProduitsExigences] = useState('');
  const [repassageFrequence, setRepassageFrequence] = useState('');
  const [repassagePrestationsComplementaires, setRepassagePrestationsComplementaires] = useState('');
  const [nettoyageVitresTypeAcces, setNettoyageVitresTypeAcces] = useState('');
  const [nettoyageVitresFrequence, setNettoyageVitresFrequence] = useState('');
  const [nettoyageTapisNombreArticles, setNettoyageTapisNombreArticles] = useState('');
  const [nettoyageTapisTypePrincipal, setNettoyageTapisTypePrincipal] = useState('');
  const [nettoyageTapisServicesAdditionnels, setNettoyageTapisServicesAdditionnels] = useState('');
  const [organisationRangementNiveau, setOrganisationRangementNiveau] = useState('');
  const [organisationRangementDuree, setOrganisationRangementDuree] = useState('');
  const [organisationRangementServicesAdditionnels, setOrganisationRangementServicesAdditionnels] =
    useState('');
  const [showerDismantleNeed, setShowerDismantleNeed] = useState('');
  const [showerDismantleElements, setShowerDismantleElements] = useState([]);
  const [bathroomLightingType, setBathroomLightingType] = useState('');
  const [bathroomLightingZone, setBathroomLightingZone] = useState('');
  const [bathroomLightingObjective, setBathroomLightingObjective] = useState('');
  const [toiletLocation, setToiletLocation] = useState('');
  const [toiletWaterSupplyReady, setToiletWaterSupplyReady] = useState('');
  const [toiletEvacuationType, setToiletEvacuationType] = useState('');
  const [bathroomStorageType, setBathroomStorageType] = useState('');
  const [bathroomStorageObjective, setBathroomStorageObjective] = useState('');
  const [waterHeaterCapacity, setWaterHeaterCapacity] = useState('');
  const [waterHeaterElectricalReady, setWaterHeaterElectricalReady] = useState('');
  const [waterHeaterWaterSupplyReady, setWaterHeaterWaterSupplyReady] = useState('');
  const [waterHeaterThermostatInstall, setWaterHeaterThermostatInstall] = useState('');
  const [masticColorRequirement, setMasticColorRequirement] = useState('');
  const [climatisationUnitType, setClimatisationUnitType] = useState('');
  const [climatisationOutletNearby, setClimatisationOutletNearby] = useState('');
  const [chauffagePeripheralCounts, setChauffagePeripheralCounts] = useState({});
  const [chauffageSupplyReady, setChauffageSupplyReady] = useState('');
  const [chaudiereEnergyType, setChaudiereEnergyType] = useState('');
  const [chaudiereUsage, setChaudiereUsage] = useState('');
  const [tuyauxRooms, setTuyauxRooms] = useState([]);
  const [kitchenPlumbingInterventionType, setKitchenPlumbingInterventionType] = useState('');
  const [kitchenPlumbingEquipment, setKitchenPlumbingEquipment] = useState('');
  const [kitchenPlumbingProblemNature, setKitchenPlumbingProblemNature] = useState('');
  const [entretienDepannageInterventionType, setEntretienDepannageInterventionType] = useState('');
  const [entretienDepannageEquipment, setEntretienDepannageEquipment] = useState('');
  const [entretienDepannageProblemNature, setEntretienDepannageProblemNature] = useState('');
  const [storesBlindCounts, setStoresBlindCounts] = useState({});
  const [storesWallType, setStoresWallType] = useState('');
  const [storeMotoriseCount, setStoreMotoriseCount] = useState('');
  const [storeMotoriseMaterial, setStoreMotoriseMaterial] = useState('');
  const [storeMotoriseElectricityReady, setStoreMotoriseElectricityReady] = useState('');
  const [portailMotoriseType, setPortailMotoriseType] = useState('');
  const [portailMotoriseMaterial, setPortailMotoriseMaterial] = useState('');
  const [portailMotorisePosts, setPortailMotorisePosts] = useState('');
  const [portailGateType, setPortailGateType] = useState('');
  const [portailMaterial, setPortailMaterial] = useState('');
  const [portailPosts, setPortailPosts] = useState('');
  const [porteGarageMaterial, setPorteGarageMaterial] = useState('');
  const [porteGarageSupportWork, setPorteGarageSupportWork] = useState('');
  const [porteGarageRemoveOld, setPorteGarageRemoveOld] = useState('');
  const [porteGarageLocking, setPorteGarageLocking] = useState('');
  const [potagerPlantCount, setPotagerPlantCount] = useState('');
  const [potagerSoilReady, setPotagerSoilReady] = useState('');
  const [fleursPlantCountRange, setFleursPlantCountRange] = useState('');
  const [fleursSoilReady, setFleursSoilReady] = useState('');
  const [feuillesType, setFeuillesType] = useState('');
  const [arbustesSoilReady, setArbustesSoilReady] = useState('');
  const [clairiereTreeHeight, setClairiereTreeHeight] = useState('');
  const [clairiereSoilReady, setClairiereSoilReady] = useState('');
  const [arbrePlantingHeight, setArbrePlantingHeight] = useState('');
  const [arbreSoilReady, setArbreSoilReady] = useState('');
  const [abattageTreeCount, setAbattageTreeCount] = useState('');
  const [abattageTreeHeight, setAbattageTreeHeight] = useState('');
  const [pelouseSoilAmendments, setPelouseSoilAmendments] = useState('');
  const [espaceVertsTools, setEspaceVertsTools] = useState('');
  const [espaceVertsRegularMaintenance, setEspaceVertsRegularMaintenance] = useState('');
  const [haiesTools, setHaiesTools] = useState('');
  const [tondreTools, setTondreTools] = useState('');
  const [tondreGrassRemoval, setTondreGrassRemoval] = useState('');
  const [porteDoorType, setPorteDoorType] = useState('');
  const [porteInterventionDetail, setPorteInterventionDetail] = useState('');
  const [porteProblemNature, setPorteProblemNature] = useState('');
  const [porteMaterial, setPorteMaterial] = useState('');
  const [ouverturePorteTypePorte, setOuverturePorteTypePorte] = useState('');
  const [ouverturePorteProblemeRencontre, setOuverturePorteProblemeRencontre] = useState('');
  const [portesSecuriseesTypePorte, setPortesSecuriseesTypePorte] = useState('');
  const [portesSecuriseesSerrure, setPortesSecuriseesSerrure] = useState('');
  const [serrurerieTypePorteSerrure, setSerrurerieTypePorteSerrure] = useState('');
  const [serrurerieProbleme, setSerrurerieProbleme] = useState('');
  const [mecaniqueEntretienPrestationPrincipale, setMecaniqueEntretienPrestationPrincipale] =
    useState('');
  const [mecaniqueChangementRouesNombre, setMecaniqueChangementRouesNombre] = useState('');
  const [mecaniqueBatterieVehicule, setMecaniqueBatterieVehicule] = useState('');
  const [mecaniqueBatterieSymptome, setMecaniqueBatterieSymptome] = useState('');
  const [mecaniqueBatterieServicesAdditionnels, setMecaniqueBatterieServicesAdditionnels] =
    useState('');
  const [mecaniquePlaquetteFreinSymptome, setMecaniquePlaquetteFreinSymptome] = useState('');
  const [mecaniquePlaquetteFreinServicesAdditionnels, setMecaniquePlaquetteFreinServicesAdditionnels] =
    useState('');
  const [mecaniqueDepannageTypePanne, setMecaniqueDepannageTypePanne] = useState('');
  const [mecaniqueDepannageUrgence, setMecaniqueDepannageUrgence] = useState('');
  const [mecaniqueDepannageDestinationRemorquage, setMecaniqueDepannageDestinationRemorquage] =
    useState('');
  const [mecaniquePiecesAutoPieceConcernee, setMecaniquePiecesAutoPieceConcernee] = useState('');
  const [mecaniqueDiagnosticNettoyageProblemePrincipal, setMecaniqueDiagnosticNettoyageProblemePrincipal] =
    useState('');
  const [fenetresFrameMaterial, setFenetresFrameMaterial] = useState('');
  const [fenetresWindowType, setFenetresWindowType] = useState('');
  const [vestiaireDoorCounts, setVestiaireDoorCounts] = useState({});
  const [vestiaireInteriorCounts, setVestiaireInteriorCounts] = useState({});
  const [menuiserieCuisineArmoiresCounts, setMenuiserieCuisineArmoiresCounts] = useState({});
  const [menuiserieCuisineLinearMeters, setMenuiserieCuisineLinearMeters] = useState('');
  const [menuiserieCuisineLayout, setMenuiserieCuisineLayout] = useState('');
  const [kitchenFaucetType, setKitchenFaucetType] = useState('');
  const [kitchenFaucetInterventionType, setKitchenFaucetInterventionType] = useState('');
  const [kitchenFaucetProblemNature, setKitchenFaucetProblemNature] = useState('');
  const [kitchenStorageType, setKitchenStorageType] = useState('');
  const [kitchenStorageObjective, setKitchenStorageObjective] = useState('');
  const [bathroomEquipment, setBathroomEquipment] = useState('');
  const [bathroomInterventionType, setBathroomInterventionType] = useState('');
  const [curtainRodWallType, setCurtainRodWallType] = useState('');
  const [shelfWallType, setShelfWallType] = useState('');
  const [assemblyFurnitureCounts, setAssemblyFurnitureCounts] = useState({});
  const [assemblyFurnitureCondition, setAssemblyFurnitureCondition] = useState('');
  const [assemblyWallFixRequired, setAssemblyWallFixRequired] = useState('');
  const [assemblyWallType, setAssemblyWallType] = useState('');
  const [fixationWallType, setFixationWallType] = useState('');
  const [tileFinish, setTileFinish] = useState([]);
  const [tileSize, setTileSize] = useState([]);
  const [surfaceState, setSurfaceState] = useState([]);
  const canContinue = isOutdoorPavingService
    ? true
    : isPlasterboardService
    ? true
    : isPlasterService
    ? true
    : isParquetService
    ? Boolean(bedroomCount && installationType && parquetExistingFloorType)
    : isTerraceService
    ? Boolean(terracePreparationNeed)
    : usesDocumentStepAtStep4
    ? uploadedFiles.length > 0
    : isPolishedConcreteService
    ? Boolean(polishedConcreteExistingFloor)
    : isCarpetService
    ? Boolean(carpetInstallType && carpetRoomEmpty)
    : isSkirtingService
    ? Boolean(baseboardType && baseboardFinish)
      : isObturateurMenuiserieService
    ? Boolean(obturateurCount && obturateurType && obturateurMaterial && obturateurMasonryWork)
      : isVoletMotoriseService
    ? Boolean(voletMotoriseCount && voletMotoriseType && voletMotoriseMaterial && voletMotoriseMasonryWork)
      : isBornesTableauxElectriquesService
    ? bornesTableauxRooms.length > 0
      : isEclairageService
    ? Boolean(eclairageWallType && eclairageElectricalModification)
    : isPrisesInterrupteurService
    ? Boolean(prisesInterrupteurCountRange && prisesInterrupteurReady)
    : isDomotiqueService
    ? Boolean(Object.values(domotiqueDeviceCounts).some((count) => count > 0))
    : isInterphoneService
    ? Boolean(interphoneSystemType && interphoneInstallType)
      : isAlarmesSecuriteService
    ? Boolean(alarmesSecuriteLieu && alarmesSecuriteFonctionnalites)
      : isCamerasSecuriteService
    ? Boolean(camerasSecuriteNombre)
      : isAlarmeCameraService
    ? Boolean(alarmeType && Object.values(alarmeElementCounts).some((count) => count > 0))
      : isLightingHomeStagingService
    ? Boolean(lightingObjective && lightingType)
    : isCustomFurnitureService
    ? customFurnitureMaterials.length > 0
    : isDecorationConsultingService
    ? Boolean(decorationConsultingStyle && decorationConsultingObjective && decorationConsultingSupportLevel)
    : isDecorationSpacePlanningService
    ? Boolean(spaceDecorationStyle && spaceDecorationObjective)
    : isWallDecorationAccessoriesService
    ? Boolean(wallDecorationElementType && wallDecorationStyle)
    : isFurnitureRepairService
    ? Boolean(furnitureRepairType && furnitureProblemNature && furnitureRepairMaterial)
    : isFurnitureTreatmentService
    ? Boolean(furnitureType && furnitureMaterial && furnitureTreatmentType)
    : isKitchenFurnitureService
    ? Boolean(Object.values(kitchenFurnitureCounts).some((count) => count > 0) && kitchenWallFixRequired)
    : isHomeApplianceService
    ? Boolean(applianceElectricalInstall && applianceWaterConnection)
    : isKitchenHoodService
    ? Boolean(kitchenHoodType && kitchenHoodDuctNeed)
    : isVmcService
    ? Boolean(vmcSystemType && vmcSystemLocation)
    : isChargementDechargementService
    ? Boolean(
        chargementGoodsTypes.length > 0 &&
          chargementAccessDifficulty.length > 0 &&
          chargementTruckDistance
      )
    : isGardeStockageMeublesService
    ? Boolean(
        gardeStockageApproxVolume &&
          gardeStockageMainGoodsType &&
          gardeStockageAdditionalServices
      )
    : isGardeAccompagnementEnfantsService
    ? Boolean(
        gardeEnfantsDureeSouhaitee &&
          gardeEnfantsFrequence &&
          gardeEnfantsBesoinParticulier
      )
    : isDebarrasMenageNettoyageService
    ? Boolean(debarrasMenageLieu && debarrasMenageAcces && debarrasMenageServicesVoulus)
    : isDebarrasService
    ? Boolean(debarrasTypeLieu && debarrasAcces && debarrasServicesSouhaites)
    : isLocationUtilitaireAvecChauffeurService
    ? Boolean(
        utilitaireChauffeurMode &&
          utilitaireLieuPriseEnCharge &&
          utilitaireVolumeTypeTransport
      )
    : isMenageSurfaceMeublesService
    ? Boolean(
        menageSurfacesSuperficieApprox &&
          menageSurfacesPrestationType &&
          menageSurfacesProduitsExigences
      )
    : isRepassageService
    ? Boolean(repassageFrequence && repassagePrestationsComplementaires)
    : isNettoyageVitresService
    ? Boolean(nettoyageVitresTypeAcces && nettoyageVitresFrequence)
    : isNettoyageTapisCanapesService
    ? Boolean(
        nettoyageTapisNombreArticles &&
          nettoyageTapisTypePrincipal &&
          nettoyageTapisServicesAdditionnels
      )
    : isOrganisationRangementService
    ? Boolean(
        organisationRangementNiveau &&
          organisationRangementDuree &&
          organisationRangementServicesAdditionnels
      )
    : isMontageDemontageMeublesService
    ? Boolean(montageDemontageMeublesCountRange)
    : isAccompagnementPersonnesAgeesService
    ? Boolean(accompagnementAinesLieu && accompagnementAinesAutonomie)
    : isShowerCabinService
    ? Boolean(showerDismantleNeed)
    : isBathroomLightingService
    ? Boolean(bathroomLightingType && bathroomLightingZone && bathroomLightingObjective)
    : isToiletService
    ? Boolean(toiletLocation && toiletWaterSupplyReady)
    : isBathroomStorageService
    ? Boolean(bathroomStorageType && bathroomStorageObjective)
    : isWaterHeaterService
    ? Boolean(waterHeaterElectricalReady && waterHeaterWaterSupplyReady && waterHeaterThermostatInstall)
    : isMasticService
    ? true
    : isClimatisationService
    ? Boolean(climatisationOutletNearby)
    : isChauffageService
    ? Boolean(Object.values(chauffagePeripheralCounts).some((count) => count > 0))
    : isChaudiereService
    ? Boolean(chaudiereEnergyType && chaudiereUsage)
    : isEntretienDepannagePlumbingService
    ? Boolean(
        entretienDepannageInterventionType &&
          entretienDepannageEquipment &&
          entretienDepannageProblemNature
      )
    : isTuyauxService
    ? tuyauxRooms.length > 0
    : isKitchenPlumbingService
    ? Boolean(
        kitchenPlumbingInterventionType &&
        kitchenPlumbingEquipment &&
        (kitchenPlumbingInterventionType !== 'Réparation' || kitchenPlumbingProblemNature)
      )
    : isKitchenFaucetService
    ? Boolean(
        kitchenFaucetType &&
        kitchenFaucetInterventionType &&
        (kitchenFaucetInterventionType !== 'Réparation' || kitchenFaucetProblemNature)
      )
    : isKitchenStorageService
    ? Boolean(kitchenStorageType && kitchenStorageObjective)
    : isBathroomService
    ? Boolean(bathroomEquipment && bathroomInterventionType)
    : isStoresMenuiserieService
    ? Boolean(Object.values(storesBlindCounts).some((count) => count > 0) && storesWallType)
    : isStoreMotoriseService
    ? Boolean(storeMotoriseCount && storeMotoriseMaterial && storeMotoriseElectricityReady)
    : isPortailMotoriseService
    ? Boolean(portailMotoriseType && portailMotoriseMaterial)
    : isPortailMenuiserieService
    ? Boolean(portailGateType)
    : isGarageMotoriseService
    ? Boolean(porteGarageMaterial && porteGarageSupportWork && porteGarageRemoveOld)
    : isPorteGarageMenuiserieService
    ? Boolean(porteGarageMaterial && porteGarageSupportWork && porteGarageRemoveOld)
    : isPotagerService
    ? Boolean(potagerSoilReady)
    : isFleursService
    ? Boolean(fleursPlantCountRange && fleursSoilReady)
    : isFeuillesService
    ? Boolean(feuillesType)
    : isArbustesService
    ? Boolean(arbustesSoilReady)
    : isClairiereService
    ? Boolean(clairiereTreeHeight && clairiereSoilReady)
    : isArbrePlantationService
    ? Boolean(arbrePlantingHeight && arbreSoilReady)
    : isAbattageArbresService
    ? Boolean(abattageTreeCount && abattageTreeHeight)
    : isPelouseService
    ? Boolean(pelouseSoilAmendments)
    : isHaiesService
    ? Boolean(haiesTools)
    : isTondreService
    ? Boolean(tondreTools && tondreGrassRemoval)
    : isEspaceVertsService
    ? Boolean(espaceVertsTools && espaceVertsRegularMaintenance)
    : isOuverturePorteService
    ? Boolean(ouverturePorteTypePorte && ouverturePorteProblemeRencontre)
    : isPortesSecuriseesService
    ? Boolean(portesSecuriseesTypePorte && portesSecuriseesSerrure)
    : isSerrurerieService
    ? Boolean(serrurerieTypePorteSerrure && serrurerieProbleme)
    : isEntretienReparationMecaniqueService
    ? Boolean(mecaniqueEntretienPrestationPrincipale)
    : isChangementRouesMecaniqueService
    ? Boolean(mecaniqueChangementRouesNombre)
    : isBatterieElectriqueMecaniqueService
    ? Boolean(
        mecaniqueBatterieVehicule &&
          mecaniqueBatterieSymptome &&
          mecaniqueBatterieServicesAdditionnels
      )
    : isPlaquetteFreinMecaniqueService
    ? Boolean(
        mecaniquePlaquetteFreinSymptome && mecaniquePlaquetteFreinServicesAdditionnels
      )
    : isDepannageRemorquageMecaniqueService
    ? Boolean(
        mecaniqueDepannageTypePanne &&
          mecaniqueDepannageUrgence &&
          mecaniqueDepannageDestinationRemorquage
      )
    : isChangementPiecesAutoMecaniqueService
    ? Boolean(mecaniquePiecesAutoPieceConcernee)
    : isDiagnosticNettoyageMecaniqueService
    ? Boolean(mecaniqueDiagnosticNettoyageProblemePrincipal)
    : isPorteMenuiserieService
    ? Boolean(
        porteDoorType &&
          porteInterventionDetail &&
          (porteInterventionDetail !== 'Réparation' || porteProblemNature) &&
          porteMaterial
      )
    : isFenetresPortesFenetresService
    ? Boolean(fenetresFrameMaterial && fenetresWindowType)
    : isVestiaireMenuiserieService
    ? Boolean(
        Object.values(vestiaireDoorCounts).some((count) => count > 0) &&
          Object.values(vestiaireInteriorCounts).some((count) => count > 0)
      )
    : isCuisineMenuiserieService
    ? Boolean(Object.values(menuiserieCuisineArmoiresCounts).some((count) => count > 0))
    : isCurtainRodService
    ? Boolean(curtainRodWallType)
    : isShelfService
    ? true
    : isFixationMuralService
    ? Boolean(fixationWallType)
    : isAssemblyService
    ? Boolean(
        Object.values(assemblyFurnitureCounts).some((count) => count > 0) &&
        assemblyFurnitureCondition &&
        assemblyWallFixRequired &&
        (assemblyWallFixRequired !== 'Oui' || assemblyWallType)
      ) 
    : isWallpaperService
    ? Boolean(wallHeight && wallCoverType)
    : isPaintingService
      ? Boolean(wallCoverRemoval && protectionAreas)
      : tileFinish.length > 0 && tileSize.length > 0 && surfaceState.length > 0;

  const pickChoice = (value, setter) => {
    setter(value);
  };

  const toggleChoice = (value, setter) => {
    setter((prev) => (prev.includes(value) ? prev.filter((x) => x !== value) : [...prev, value]));
  };
  const updateAssemblyCount = (item, delta) => {
    setAssemblyFurnitureCounts((prev) => {
      const current = prev[item] || 0;
      const next = Math.max(0, current + delta);
      return { ...prev, [item]: next };
    });
  };
  const updateKitchenCount = (item, delta) => {
    setKitchenFurnitureCounts((prev) => {
      const current = prev[item] || 0;
      const next = Math.max(0, current + delta);
      return { ...prev, [item]: next };
    });
  };
  const updateStoresBlindCount = (item, delta) => {
    setStoresBlindCounts((prev) => {
      const current = prev[item] || 0;
      const next = Math.max(0, current + delta);
      return { ...prev, [item]: next };
    });
  };
  const updateAlarmeElementCount = (item, delta) => {
    setAlarmeElementCounts((prev) => {
      const current = prev[item] || 0;
      const next = Math.max(0, current + delta);
      return { ...prev, [item]: next };
    });
  };
  const updateDomotiqueDeviceCount = (item, delta) => {
    setDomotiqueDeviceCounts((prev) => {
      const current = prev[item] || 0;
      const next = Math.max(0, current + delta);
      return { ...prev, [item]: next };
    });
  };
  const updateVestiaireDoorCount = (item, delta) => {
    setVestiaireDoorCounts((prev) => {
      const current = prev[item] || 0;
      const next = Math.max(0, current + delta);
      return { ...prev, [item]: next };
    });
  };
  const updateVestiaireInteriorCount = (item, delta) => {
    setVestiaireInteriorCounts((prev) => {
      const current = prev[item] || 0;
      const next = Math.max(0, current + delta);
      return { ...prev, [item]: next };
    });
  };
  const updateMenuiserieCuisineArmoiresCount = (item, delta) => {
    setMenuiserieCuisineArmoiresCounts((prev) => {
      const current = prev[item] || 0;
      const next = Math.max(0, current + delta);
      return { ...prev, [item]: next };
    });
  };
  const updateChauffagePeripheralCount = (item, delta) => {
    setChauffagePeripheralCounts((prev) => {
      const current = prev[item] || 0;
      const next = Math.max(0, current + delta);
      return { ...prev, [item]: next };
    });
  };
  const uploadSubtitle = useMemo(() => {
    if (!uploadedFiles.length) return t('wizard.step5Hint');
    return t('wizard.step5FilesSelected', { count: uploadedFiles.length });
  }, [uploadedFiles.length, t]);

  const handleFiles = (event) => {
    const incoming = Array.from(event.target.files || []);
    if (!incoming.length) return;
    const merged = [...uploadedFiles];
    let nextError = '';
    incoming.forEach((file) => {
      if (merged.length >= MAX_FILES) {
        nextError = t('wizard.step5MaxFiles');
        return;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        nextError = t('wizard.step5FileTooLarge', { name: file.name });
        return;
      }
      merged.push(file);
    });
    setUploadedFiles(merged);
    setUploadError(nextError);
    event.target.value = '';
  };

  const removeFileAt = (indexToRemove) => {
    setUploadedFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
    setUploadError('');
  };

  const sizeCards = [
    { title: 'Taille standard', image: '/images/taillestandard.jpg' },
    { title: 'Grande taille', image: '/images/grandetaille.jpg' },
    { title: 'Petite taille', image: '/images/petitetaille.jpg' },
    { title: "À définir avec l'artisan", image: '/images/carrelage.jpg' },
  ];

  return (
    <AppLayout title={t('wizard.step4Title')} rootClass="layout-1">
      <style>{`
        .request-step4-page {
          background: #f2f3f5;
          min-height: 78vh;
          border-radius: 14px;
          position: relative;
          display: flex;
          flex-direction: column;
        }
        .request-step4-content {
          flex: 1 1 auto;
          width: 100%;
          max-width: 760px;
          margin: 0 auto;
          padding-top: 4.2rem;
          padding-bottom: 2rem;
        }
        .request-step4-step {
          color: #697586;
          font-size: 0.9rem;
          margin-bottom: 0.25rem;
        }
        .request-step4-heading {
          color: #0f2b3d;
          font-weight: 700;
          margin-bottom: 2rem;
        }
        .request-step4-question {
          color: #0f2b3d;
          font-size: 1.05rem;
          font-weight: 500;
          margin-bottom: 1rem;
        }
        .request-step4-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 0.75rem;
        }
        .request-step4-grid--services-one-line {
          display: flex;
          flex-wrap: nowrap;
          gap: 0.75rem;
        }
        .request-step4-grid--services-one-line .request-step4-choice {
          flex: 1 1 0;
          min-width: 0;
        }
        .request-step4-grid--panne-ligne1 {
          grid-template-columns: repeat(4, minmax(0, 1fr));
        }
        .request-step4-grid--panne-ligne2 {
          margin-top: 0.75rem;
          grid-template-columns: repeat(3, minmax(0, 1fr));
        }
        .request-step4-grid--diag-probleme-ligne1 {
          grid-template-columns: repeat(3, minmax(0, 1fr));
        }
        .request-step4-grid--diag-probleme-ligne2 {
          margin-top: 0.75rem;
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }
        .request-step4-choice {
          min-height: 52px;
          border: 1px solid #d6dde5;
          border-radius: 8px;
          background: #fff;
          color: #0f2b3d;
          text-align: left;
          padding: 0.75rem 0.9rem;
          font-weight: 500;
        }
        .request-step4-input {
          width: 100%;
          border: 1px solid #d6dde5;
          border-radius: 8px;
          height: 44px;
          padding: 0 0.8rem;
          color: #0f2b3d;
          background: #fff;
        }
        .request-step4-select {
          width: 100%;
          border: 1px solid #d6dde5;
          border-radius: 8px;
          height: 44px;
          padding: 0 0.8rem;
          color: #0f2b3d;
          background: #fff;
        }
        .request-step4-choice.is-active,
        .request-step4-size.is-active {
          border-color: #2563eb;
          box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.12);
        }
        .request-step4-size-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 0.75rem;
        }
        .request-step4-size-grid--store-motorise {
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 0.9rem;
        }
        .request-step4-size-grid--store-motorise .request-step4-size {
          min-height: 152px;
          display: flex;
          flex-direction: column;
        }
        .request-step4-size-grid--store-motorise .request-step4-size img,
        .request-step4-size-grid--store-motorise .request-step4-icon-only {
          height: 84px;
          object-fit: contain;
          background: #f8fafc;
          border-radius: 6px;
        }
        .request-step4-size-grid--store-motorise .request-step4-size-label {
          margin-top: auto;
          font-size: 0.9rem;
        }
        .request-step4-size {
          border: 1px solid #d6dde5;
          border-radius: 8px;
          background: #fff;
          text-align: left;
          padding: 0.6rem;
          min-height: 160px;
        }
        .request-step4-size img {
          width: 100%;
          height: 78px;
          object-fit: cover;
          border-radius: 6px;
          margin-bottom: 0.6rem;
        }
        .request-step4-size-label {
          color: #0f2b3d;
          font-weight: 500;
          font-size: 0.95rem;
          line-height: 1.35;
        }
        .request-step4-icon-only {
          height: 78px;
          display: flex;
          align-items: center;
          margin-bottom: 0.6rem;
        }
        .request-step4-footer {
          flex-shrink: 0;
          margin-top: auto;
          border-top: 1px solid #dde3ea;
          padding: 0.7rem 1.1rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #f8f9fb;
          border-bottom-left-radius: 14px;
          border-bottom-right-radius: 14px;
          z-index: 5;
          box-shadow: 0 -4px 16px rgba(15, 43, 61, 0.08);
        }
        .request-step4-dropzone {
          width: 100%;
          border: 1px solid #d6dde5;
          border-radius: 10px;
          background: #fff;
          padding: 1rem 1.1rem;
          display: flex;
          align-items: center;
          gap: 0.9rem;
          cursor: pointer;
          text-align: left;
        }
        .request-step4-icon {
          width: 56px;
          height: 56px;
          border-radius: 8px;
          background: #edf2f7;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #2563eb;
          font-size: 1.4rem;
          flex-shrink: 0;
        }
        .request-step4-dropzone-title {
          color: #0f2b3d;
          font-weight: 600;
          margin-bottom: 0.2rem;
        }
        .request-step4-dropzone-subtitle {
          color: #5f6b7a;
          font-size: 0.92rem;
          margin: 0;
        }
        .request-step4-list {
          margin-top: 1rem;
          display: grid;
          gap: 0.6rem;
        }
        .request-step4-item {
          border: 1px solid #d6dde5;
          border-radius: 8px;
          background: #fff;
          padding: 0.6rem 0.8rem;
          display: flex;
          justify-content: space-between;
          gap: 0.7rem;
          align-items: center;
        }
        .request-step4-file {
          color: #0f2b3d;
          font-weight: 500;
          word-break: break-word;
        }
        .request-step4-meta {
          color: #6f7d8c;
          font-size: 0.86rem;
        }
        .request-step4-remove {
          border: none;
          background: transparent;
          color: #d14343;
          font-weight: 600;
          padding: 0;
        }
        .request-step4-error {
          margin-top: 0.8rem;
          color: #d14343;
          font-size: 0.9rem;
        }
        .request-step4-assembly-list {
          display: grid;
          gap: 0.55rem;
        }
        .request-step4-assembly-item {
          border: 1px solid #d6dde5;
          border-radius: 8px;
          background: #fff;
          min-height: 50px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.7rem;
          padding: 0.65rem 0.7rem 0.65rem 0.9rem;
          text-align: left;
          color: #0f2b3d;
          font-weight: 500;
        }
        .request-step4-assembly-item.is-active {
          border-color: #2563eb;
          box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.12);
        }
        .request-step4-assembly-controls {
          display: flex;
          align-items: center;
          gap: 0.35rem;
        }
        .request-step4-assembly-btn {
          width: 18px;
          height: 18px;
          border-radius: 4px;
          background: #2563eb;
          color: #fff;
          font-size: 0.82rem;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          line-height: 1;
          border: none;
          padding: 0;
        }
        .request-step4-assembly-btn:disabled {
          background: #9db6ee;
          cursor: not-allowed;
        }
        .request-step4-assembly-count {
          min-width: 1.15rem;
          text-align: center;
          color: #0f2b3d;
          font-weight: 600;
        }
        @media (max-width: 992px) {
          .request-step4-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          .request-step4-grid--panne-ligne1 {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          .request-step4-grid--panne-ligne2 {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }
          .request-step4-grid--diag-probleme-ligne1 {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          .request-step4-grid--diag-probleme-ligne2 {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          .request-step4-size-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          .request-step4-size-grid--store-motorise {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }
        }
        @media (max-width: 575px) {
          .request-step4-grid,
          .request-step4-size-grid {
            grid-template-columns: 1fr;
          }
          .request-step4-grid--panne-ligne1,
          .request-step4-grid--panne-ligne2 {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          .request-step4-grid--diag-probleme-ligne1,
          .request-step4-grid--diag-probleme-ligne2 {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          .request-step4-grid--services-one-line {
            flex-wrap: nowrap;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            padding-bottom: 4px;
            margin-left: -2px;
            margin-right: -2px;
            padding-left: 2px;
            padding-right: 2px;
          }
          .request-step4-grid--services-one-line .request-step4-choice {
            flex: 0 0 auto;
            min-width: 7.5rem;
          }
          .request-step4-size-grid--store-motorise {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 0.6rem;
          }
          .request-step4-size-grid--store-motorise .request-step4-size {
            min-height: 144px;
          }
          .request-step4-size-grid--store-motorise .request-step4-size img,
          .request-step4-size-grid--store-motorise .request-step4-icon-only {
            height: 72px;
          }
        }
      `}</style>

      <NioSection className="pt-120 pt-lg-160 pb-4">
        <NioSection.Content>
          <div className="request-step4-page p-3 p-md-4">
            <div className="request-step4-content">
              <div className="request-step4-step">{t('wizard.step', { n: 4 })}</div>
              <h2 className="request-step4-heading">
                {tw(
                  isCurtainRodService
                    ? 'Informations générales'
                    : isCuisineMenuiserieService
                      ? 'Armoires'
                      : isChargementDechargementService ||
                          isMontageDemontageMeublesService ||
                          isGardeStockageMeublesService ||
                          isGardeAccompagnementEnfantsService ||
                          isDebarrasService ||
                          isLocationUtilitaireAvecChauffeurService ||
                          isMenageSurfaceMeublesService ||
                          isRepassageService ||
                          isNettoyageVitresService ||
                          isOrganisationRangementService ||
                          isPortesSecuriseesService ||
                          isVmcService ||
                          isHaiesService
                        ? 'Détails supplémentaires'
                        : 'Détails',
                )}
              </h2>

              {isOutdoorPavingService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Quel est le format de votre revêtement de sol ? (facultatif)")}</h3>
                    <div className="request-step4-grid">
                      {['Pavé', 'Dalle', 'Latte', "À définir avec l'artisan"].map((option) => (
                        <button
                          key={option}  
                          type="button"
                          className={`request-step4-choice ${floorCoverFormat === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setFloorCoverFormat)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Des travaux préparatoires sont-ils à effectuer avant l'installation ? (facultatif)")}</h3>
                    <div className="request-step4-grid">
                      {['Travaux de terrassement', 'Installation du drainage', 'Isolation', "À définir avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${preparatoryWork === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setPreparatoryWork)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isTerraceService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("La terrasse sera installée le (optionnel)")}</h3>
                    <div className="request-step4-grid">
                      {['dalle de béton', 'socles réglables', 'lit de sable', "À discuter avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${terraceInstallationBase === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setTerraceInstallationBase)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Des travaux de préparation du terrain sont-ils nécessaires avant l'installation ?")}</h3>
                    <div className="request-step4-grid">
                      {['Oui', 'Non', "À discuter avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${terracePreparationNeed === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setTerracePreparationNeed)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : usesDocumentStepAtStep4 ? (
                <>
                  <p className="request-step4-question mb-4">{tw("Ajoutez au moins un document pour continuer.")}</p>
                  <label htmlFor="request-step4-files" className="request-step4-dropzone">
                    <div className="request-step4-icon">+</div>
                    <div>
                      <div className="request-step4-dropzone-title">{t('wizard.step5Dropzone')}</div>
                      <p className="request-step4-dropzone-subtitle">{uploadSubtitle}</p>
                    </div>
                  </label>
                  <input
                    id="request-step4-files"
                    type="file"
                    accept="image/*,.pdf"
                    multiple
                    onChange={handleFiles}
                    hidden
                  />
                  {uploadedFiles.length > 0 && (
                    <div className="request-step4-list">
                      {uploadedFiles.map((file, index) => (
                        <div key={`${file.name}-${file.size}-${index}`} className="request-step4-item">
                          <div>
                            <div className="request-step4-file">{file.name}</div>
                            <div className="request-step4-meta">{formatFileSize(file.size, t)}</div>
                          </div>
                          <button
                            type="button"
                            className="request-step4-remove"
                            onClick={() => removeFileAt(index)}
                          >
                            {t('common.remove')}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {uploadError && <div className="request-step4-error">{uploadError}</div>}
                </>
              ) : isPolishedConcreteService ? (
                <>
                  <div>
                    <h3 className="request-step4-question">{tw("Quel type de revêtement de sol est actuellement installé ?")}</h3>
                    <div className="request-step4-size-grid">
                      {[
                        { title: 'Béton', image: '/images/beton2.webp' },
                        { title: 'Tuile', image: '/images/tuile.jpg' },
                        { title: 'Bois dur', image: '/images/bois.jpg' },
                        { title: 'Vinyle', image: '/images/parquet.jpg' },
                        { title: 'Tapis', image: '/images/tapis.jpg' },
                        { title: "À définir avec l'artisan", image: '/images/artisan.jpg' },
                      ].map((option) => (
                        <button
                          key={option.title}
                          type="button"
                          className={`request-step4-size ${polishedConcreteExistingFloor === option.title ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option.title, setPolishedConcreteExistingFloor)}
                        >
                          <img src={option.image} alt={tw(option.title)} />
                          <div className="request-step4-size-label">{tw(option.title)}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isBornesTableauxElectriquesService ? (
                <>
                  <div>
                    <h3 className="request-step4-question">{tw("Quelles pièces sont concernées ?")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Salon',
                        'Chambre à coucher',
                        'Couloir',
                        'Cuisine',
                        'Salle de bain',
                        'Grenier',
                        'Extérieur',
                        'Autre',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${bornesTableauxRooms.includes(option) ? 'is-active' : ''}`}
                          onClick={() => toggleChoice(option, setBornesTableauxRooms)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isEclairageService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("De quel type de mur s'agit-il ?")}</h3>
                    <div className="request-step4-size-grid">
                      {[
                        { title: 'Béton', image: '/images/beton2.webp' },
                        { title: 'Brique', image: '/images/brique.jpg' },
                        { title: 'Bois', image: '/images/bois.jpg' },
                        { title: 'cloisons sèches', image: '/images/cloisons.jpg' },
                        { title: 'Carrelage', image: '/images/tuile.jpg' },
                        { title: "À définir avec l'artisan", image: '/images/artisan.jpg' },
                      ].map((option) => (
                        <button
                          key={option.title}
                          type="button"
                          className={`request-step4-size ${eclairageWallType === option.title ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option.title, setEclairageWallType)}
                        >
                          <img src={option.image} alt={tw(option.title)} />
                          <div className="request-step4-size-label">{tw(option.title)}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Des modifications électriques (passage de câbles, goulottes murales,...) sont-elles nécessaires ?")}</h3>
                    <div className="request-step4-grid">
                      {['Oui', 'Non', "À définir avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${eclairageElectricalModification === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setEclairageElectricalModification)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isLightingHomeStagingService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Objectif principal :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Améliorer la luminosité',
                        'Créer une ambiance chaleureuse',
                        'Valoriser un bien pour vente/location',
                        'Mettre en avant certains éléments',
                        'À définir avec le prestataire',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${lightingObjective === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setLightingObjective)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Type d’éclairage souhaité :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Éclairage général',
                        'Éclairage d’ambiance',
                        'Éclairage décoratif',
                        'Spots / LED',
                        'Mélange de plusieurs types',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${lightingType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setLightingType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isCustomFurnitureService ? (
                <>
                  <div>
                    <h3 className="request-step4-question">{tw("Quel(s) matériau(x) préférez-vous pour la fabrication ?")}</h3>
                    <div className="request-step4-size-grid">
                      {[
                        { title: 'MDF', image: '/images/mdf.jpg' },
                        { title: 'Bois massif', image: '/images/bois.jpg' },
                        { title: 'Métal', image: '/images/beton2.webp' },
                        { title: 'Verre', image: '/images/vinyle.jpg' },
                        { title: 'Autre' },
                      ].map((option) => (
                        <button
                          key={option.title}
                          type="button"
                          className={`request-step4-size ${customFurnitureMaterials.includes(option.title) ? 'is-active' : ''}`}
                          onClick={() => toggleChoice(option.title, setCustomFurnitureMaterials)}
                        >
                          {option.image ? <img src={option.image} alt={tw(option.title)} /> : <div className="request-step4-size-label">...</div>}
                          <div className="request-step4-size-label">{tw(option.title)}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isDecorationConsultingService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Style souhaité :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Moderne',
                        'Classique',
                        'Minimaliste',
                        'Industriel',
                        'Scandinave',
                        'Bohème',
                        'À définir avec le prestataire',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${decorationConsultingStyle === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setDecorationConsultingStyle)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Objectif principal :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Embellir l’espace',
                        'Gagner en fonctionnalité',
                        'Créer une ambiance spécifique',
                        'Moderniser la décoration',
                        'À définir',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${decorationConsultingObjective === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setDecorationConsultingObjective)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Niveau d’accompagnement souhaité :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Simple conseil (oral / rapide)',
                        'Recommandations détaillées',
                        'Plan d’aménagement',
                        'Suivi complet du projet',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${decorationConsultingSupportLevel === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setDecorationConsultingSupportLevel)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isDecorationSpacePlanningService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Style souhaité :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Moderne',
                        'Classique',
                        'Minimaliste',
                        'Industriel',
                        'Scandinave',
                        'Bohème',
                        'À définir avec le prestataire',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${spaceDecorationStyle === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setSpaceDecorationStyle)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Objectif principal :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Esthétique (embellir)',
                        'Gain d’espace',
                        'Fonctionnalité',
                        'Confort',
                        'Mise en location / vente (home staging)',
                        'À définir',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${spaceDecorationObjective === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setSpaceDecorationObjective)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isWallDecorationAccessoriesService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Type d’éléments à installer :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Cadres / Tableaux',
                        'Miroirs',
                        'Étagères décoratives',
                        'Horloges murales',
                        'Stickers muraux',
                        'Objets décoratifs',
                        'Autre',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${wallDecorationElementType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setWallDecorationElementType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Style souhaité :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Moderne',
                        'Classique',
                        'Minimaliste',
                        'Industriel',
                        'Bohème',
                        'À définir avec l’artisan',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${wallDecorationStyle === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setWallDecorationStyle)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isFurnitureRepairService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Type de meuble :")}</h3>
                    <div className="request-step4-grid">
                      {['Table', 'Chaise', 'Armoire / Dressing', 'Lit', 'Canapé', 'Meuble TV', 'Autre'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${furnitureRepairType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setFurnitureRepairType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Nature du problème :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Cassé (structure)',
                        'Instable / bancal',
                        'Tiroir bloqué',
                        'Porte qui ne ferme pas',
                        'Charnière abîmée',
                        'Rayures / usure',
                        'Autre',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${furnitureProblemNature === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setFurnitureProblemNature)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Matériau du meuble :")}</h3>
                    <div className="request-step4-grid">
                      {['Bois massif', 'MDF / Aggloméré', 'Métal', 'Mixte', 'Je ne sais pas'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${furnitureRepairMaterial === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setFurnitureRepairMaterial)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isFurnitureTreatmentService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Type de meuble :")}</h3>
                    <div className="request-step4-grid">
                      {['Table', 'Chaise', 'Armoire / Dressing', 'Lit', 'Meuble ancien', 'Meuble extérieur', 'Autre'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${furnitureType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setFurnitureType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Matériau du meuble :")}</h3>
                    <div className="request-step4-grid">
                      {['Bois massif', 'MDF / Aggloméré', 'Métal + bois', 'Rotin / osier', 'Je ne sais pas'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${furnitureMaterial === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setFurnitureMaterial)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Type de traitement souhaité :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Nettoyage en profondeur',
                        'Anti-insectes (vers du bois, termites)',
                        'Anti-humidité / moisissure',
                        'Protection (vernis, cire, huile)',
                        'Rénovation (ponçage, peinture)',
                        "À définir avec l’artisan",
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${furnitureTreatmentType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setFurnitureTreatmentType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isKitchenFurnitureService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("De quel type de meubles s'agit-il ? Veuillez également préciser la quantité (facultatif).")}</h3>
                    <div className="request-step4-assembly-list">
                      {['Unité de base', 'Unité murale', 'Unité haute', 'Îlot de cuisine', 'Tiroir', 'Autre'].map((option) => (
                        <div
                          key={option}
                          className={`request-step4-assembly-item ${(kitchenFurnitureCounts[option] || 0) > 0 ? 'is-active' : ''}`}
                        >
                          <span>{tw(option)}</span>
                          <div className="request-step4-assembly-controls">
                            <button
                              type="button"
                              className="request-step4-assembly-btn"
                              onClick={() => updateKitchenCount(option, -1)}
                              disabled={(kitchenFurnitureCounts[option] || 0) === 0}
                            >
                              -
                            </button>
                            <span className="request-step4-assembly-count">{kitchenFurnitureCounts[option] || 0}</span>
                            <button
                              type="button"
                              className="request-step4-assembly-btn"
                              onClick={() => updateKitchenCount(option, 1)}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Faut-il fixer des objets au mur ?")}</h3>
                    <div className="request-step4-grid">
                      {['Oui', 'Non'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${kitchenWallFixRequired === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setKitchenWallFixRequired)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isHomeApplianceService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Une installation électrique est-elle nécessaire ?")}</h3>
                    <div className="request-step4-grid">
                      {['Oui', 'Non', "À définir avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${applianceElectricalInstall === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setApplianceElectricalInstall)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Faut-il encore installer les raccordements au réseau d’eau potable ?")}</h3>
                    <div className="request-step4-grid">
                      {['Oui', 'Non', "À définir avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${applianceWaterConnection === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setApplianceWaterConnection)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isKitchenHoodService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Précisez le type de hotte")}</h3>
                    <div className="request-step4-grid">
                      {['hotte aspirante', 'hotte filtrante', 'Autre'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${kitchenHoodType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setKitchenHoodType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Faut-il prévoir un conduit d’évacuation pour la hotte ? (facultatif)")}</h3>
                    <div className="request-step4-grid">
                      {['Oui', 'Non', "À déterminer avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${kitchenHoodDuctNeed === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setKitchenHoodDuctNeed)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isVmcService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("De quel type de VMC double flux s'agit-il ?")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Système à flux unique',
                        'Système à double flux',
                        'Système à humidité contrôlée',
                        "extracteur d'air individuel",
                        "À discuter avec l'artisan",
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${vmcSystemType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setVmcSystemType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Où se situe le système ?")}</h3>
                    <select
                      className="request-step4-select"
                      value={vmcSystemLocation}
                      onChange={(e) => setVmcSystemLocation(e.target.value)}
                    >
                      <option value="" disabled>{tw('Sélectionnez une réponse')}</option>
                      <option value="Dans le grenier">{tw('Dans le grenier')}</option>
                      <option value="Dans la cave">{tw('Dans la cave')}</option>
                      <option value="Dans un local technique">{tw('Dans un local technique')}</option>
                      <option value="Dans la cuisine">{tw('Dans la cuisine')}</option>
                      <option value="À discuter avec l'artisan">{tw("À discuter avec l'artisan")}</option>
                    </select>
                  </div>
                </>
              ) : isChargementDechargementService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Précisions sur le chargement / déchargement")}</h3>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Type de biens principaux à transporter : (sélection multiple possible)")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Meubles standards',
                        'Électroménager',
                        'Meubles volumineux / lourds',
                        'Objets fragiles',
                        'Piano / Instrument',
                        'Coffre-fort',
                        'Cartons seulement',
                        'Autre',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${chargementGoodsTypes.includes(option) ? 'is-active' : ''}`}
                          onClick={() => toggleChoice(option, setChargementGoodsTypes)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Y a-t-il des accès difficiles ?")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Rue étroite',
                        "Pas d'ascenseur",
                        'Stationnement difficile',
                        'Couloir étroit / escalier en colimaçon',
                        "Pas d'accès véhicule",
                        "À discuter avec l'artisan",
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${chargementAccessDifficulty.includes(option) ? 'is-active' : ''}`}
                          onClick={() => toggleChoice(option, setChargementAccessDifficulty)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Distance entre le camion et l&apos;entrée (approximative) :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Moins de 10 mètres',
                        'Entre 10 et 30 mètres',
                        'Plus de 30 mètres',
                        'À préciser',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${chargementTruckDistance === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setChargementTruckDistance)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isMontageDemontageMeublesService ? (
                <>
                  <div>
                    <h3 className="request-step4-question">{tw("Nombre de meubles à démonter / monter :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        '1 à 5 meubles',
                        '6 à 10 meubles',
                        '11 à 20 meubles',
                        'Plus de 20 meubles',
                        'À préciser',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${montageDemontageMeublesCountRange === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setMontageDemontageMeublesCountRange)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isAccompagnementPersonnesAgeesService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Lieu principal :")}</h3>
                    <div className="request-step4-grid">
                      {['Domicile', 'Extérieur', 'Hôpital / Clinique', 'À discuter'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${accompagnementAinesLieu === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setAccompagnementAinesLieu)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Niveau d&apos;autonomie ou besoins :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Autonomie complète',
                        'Autonomie partielle',
                        "Besoin d'aide au quotidien",
                        "À discuter avec l'artisan",
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${accompagnementAinesAutonomie === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setAccompagnementAinesAutonomie)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isGardeStockageMeublesService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Volume approximatif :")}</h3>
                    <div className="request-step4-grid">
                      {['Petit', 'Moyen', 'Grand', 'Très grand'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${gardeStockageApproxVolume === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setGardeStockageApproxVolume)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Type de biens principaux :")}</h3>
                    <div className="request-step4-grid">
                      {['Meubles', 'Cartons', 'Électroménager', 'Objets de valeur'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${gardeStockageMainGoodsType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setGardeStockageMainGoodsType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Services additionnels :")}</h3>
                    <div className="request-step4-grid request-step4-grid--services-one-line">
                      {['Avec transport', 'Sans transport', "À discuter avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${gardeStockageAdditionalServices === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setGardeStockageAdditionalServices)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isGardeAccompagnementEnfantsService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Durée souhaitée :")}</h3>
                    <div className="request-step4-grid">
                      {['Quelques heures', 'Demi-journée', 'Journée complète', 'Nuit'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${gardeEnfantsDureeSouhaitee === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setGardeEnfantsDureeSouhaitee)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Fréquence :")}</h3>
                    <div className="request-step4-grid">
                      {['Ponctuel', 'Régulier', "À discuter avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${gardeEnfantsFrequence === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setGardeEnfantsFrequence)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Besoin particulier :")}</h3>
                    <div className="request-step4-grid">
                      {['Allergies / Régime spécial', 'Aide aux devoirs', 'Aucun'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${gardeEnfantsBesoinParticulier === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setGardeEnfantsBesoinParticulier)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isDebarrasMenageNettoyageService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Lieu :")}</h3>
                    <div className="request-step4-grid">
                      {['Appartement', 'Maison', 'Cave/Grenier/Garage'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${debarrasMenageLieu === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setDebarrasMenageLieu)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Accès :")}</h3>
                    <div className="request-step4-grid">
                      {['Avec ascenseur', 'Sans ascenseur', 'Accès difficile'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${debarrasMenageAcces === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setDebarrasMenageAcces)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Services voulus :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Nettoyage après',
                        'Tri & Recyclage',
                        "À discuter avec l'artisan",
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${debarrasMenageServicesVoulus === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setDebarrasMenageServicesVoulus)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isDebarrasService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Type de lieu :")}</h3>
                    <div className="request-step4-grid">
                      {['Appartement', 'Maison', 'Cave/Grenier', 'Garage'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${debarrasTypeLieu === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setDebarrasTypeLieu)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Accès :")}</h3>
                    <div className="request-step4-grid">
                      {['Avec ascenseur', 'Sans ascenseur', 'Accès difficile', 'À discuter'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${debarrasAcces === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setDebarrasAcces)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Services souhaités :")}</h3>
                    <div className="request-step4-grid">
                      {['Tri + recyclage', 'Nettoyage', 'Tout évacuer'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${debarrasServicesSouhaites === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setDebarrasServicesSouhaites)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isLocationUtilitaireAvecChauffeurService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Avec chauffeur :")}</h3>
                    <div className="request-step4-grid">
                      {['Oui', 'Oui + aide chargement', 'À discuter'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${utilitaireChauffeurMode === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setUtilitaireChauffeurMode)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Lieu de prise en charge :")}</h3>
                    <div className="request-step4-grid">
                      {['Domicile', 'Autre adresse', 'À discuter'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${utilitaireLieuPriseEnCharge === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setUtilitaireLieuPriseEnCharge)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Volume / Type de transport :")}</h3>
                    <div className="request-step4-grid">
                      {['Petit volume', 'Gros volume', 'Meubles', 'Matériaux'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${utilitaireVolumeTypeTransport === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setUtilitaireVolumeTypeTransport)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isMenageSurfaceMeublesService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Superficie approximative :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Moins de 50 m²',
                        '50 - 80 m²',
                        '80 - 120 m²',
                        'Plus de 120 m²',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${menageSurfacesSuperficieApprox === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setMenageSurfacesSuperficieApprox)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Type de prestation :")}</h3>
                    <div className="request-step4-grid">
                      {['Standard', 'En profondeur', 'Avec rangement'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${menageSurfacesPrestationType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setMenageSurfacesPrestationType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Produits &amp; exigences :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Produits écologiques',
                        'Produits fournis par le client',
                        "À discuter avec l'artisan",
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${menageSurfacesProduitsExigences === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setMenageSurfacesProduitsExigences)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isRepassageService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Fréquence :")}</h3>
                    <div className="request-step4-grid">
                      {['Ponctuel', 'Régulier', "À discuter avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${repassageFrequence === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setRepassageFrequence)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Prestations complémentaires :")}</h3>
                    <div className="request-step4-grid">
                      {['Ramassage & Livraison', 'Pliage parfait', 'Repassage délicat'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${repassagePrestationsComplementaires === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setRepassagePrestationsComplementaires)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isNettoyageVitresService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Type d&apos;accès :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Rez-de-chaussée',
                        'Avec balcon',
                        'Hauteur (étages)',
                        'Difficile',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${nettoyageVitresTypeAcces === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setNettoyageVitresTypeAcces)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Fréquence :")}</h3>
                    <div className="request-step4-grid">
                      {['Ponctuel', 'Régulier', "À discuter avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${nettoyageVitresFrequence === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setNettoyageVitresFrequence)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isNettoyageTapisCanapesService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Nombre d&apos;articles :")}</h3>
                    <div className="request-step4-grid">
                      {['1', '2-3', '4 ou plus'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${nettoyageTapisNombreArticles === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setNettoyageTapisNombreArticles)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Type principal :")}</h3>
                    <div className="request-step4-grid">
                      {['Canapé tissu', 'Canapé cuir', 'Tapis'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${nettoyageTapisTypePrincipal === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setNettoyageTapisTypePrincipal)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Services additionnels :")}</h3>
                    <div className="request-step4-grid request-step4-grid--services-one-line">
                      {[
                        'Imperméabilisation',
                        'Désodorisation',
                        "À discuter avec l'artisan",
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${nettoyageTapisServicesAdditionnels === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setNettoyageTapisServicesAdditionnels)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isOrganisationRangementService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Niveau d&apos;intervention :")}</h3>
                    <div className="request-step4-grid">
                      {['Léger', 'Complet', 'Professionnel'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${organisationRangementNiveau === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setOrganisationRangementNiveau)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Durée estimée :")}</h3>
                    <div className="request-step4-grid">
                      {['½ journée', '1 journée', 'Plusieurs jours'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${organisationRangementDuree === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setOrganisationRangementDuree)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Services additionnels :")}</h3>
                    <div className="request-step4-grid request-step4-grid--services-one-line">
                      {[
                        'Achat de rangements',
                        'Étiquetage',
                        "À discuter avec l'artisan",
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${organisationRangementServicesAdditionnels === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setOrganisationRangementServicesAdditionnels)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isShowerCabinService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Avez-vous besoin d'un service de démontage ?")}</h3>
                    <div className="request-step4-grid">
                      {['Oui', 'Non'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${showerDismantleNeed === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setShowerDismantleNeed)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {showerDismantleNeed === 'Oui' && (
                    <div>
                      <h3 className="request-step4-question">{tw("Quels éléments de salle de bains doivent être démontés ?")}</h3>
                      <div className="request-step4-list">
                        {[
                          'Pare-baignoire',
                          'Cabine de douche',
                          'Lavabo',
                          'Double vasque',
                          'Miroir',
                          'Barre de douche',
                          'porte-serviettes',
                          'Autres',
                        ].map((option) => (
                          <button
                            key={option}
                            type="button"
                            className={`request-step4-choice ${showerDismantleElements.includes(option) ? 'is-active' : ''}`}
                            onClick={() => toggleChoice(option, setShowerDismantleElements)}
                          >
                            {tw(option)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : isBathroomLightingService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Type d’éclairage :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Plafonnier',
                        'Spot encastré',
                        'Applique murale (miroir)',
                        'LED / bande lumineuse',
                        'Éclairage miroir intégré',
                        'Autre',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${bathroomLightingType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setBathroomLightingType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Zone à éclairer :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Miroir / lavabo',
                        'Douche',
                        'Plafond (éclairage général)',
                        'Meuble / rangement',
                        'Autre',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${bathroomLightingZone === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setBathroomLightingZone)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Objectif principal :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Éclairage fonctionnel (visage, maquillage…)',
                        'Ambiance (lumière douce)',
                        'Sécurité (zones humides)',
                        'Décoratif',
                        'À définir',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${bathroomLightingObjective === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setBathroomLightingObjective)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isToiletService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Où se trouvent les toilettes ?")}</h3>
                    <div className="request-step4-grid">
                      {['Salle de bain', 'Toilettes séparées', 'Autre'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${toiletLocation === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setToiletLocation)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("L'approvisionnement en eau est-il déjà en place ?")}</h3>
                    <div className="request-step4-grid">
                      {['Oui', 'Non', "À définir avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${toiletWaterSupplyReady === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setToiletWaterSupplyReady)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Quel est le type d'évacuation actuel (facultatif) ?")}</h3>
                    <div className="request-step4-grid">
                      {['Évacuation horizontale', 'Évacuation verticale', 'Il me faut un broyeur', "À définir avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${toiletEvacuationType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setToiletEvacuationType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isBathroomStorageService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Type de meubles / rangements :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Meuble sous vasque',
                        'Colonne de rangement',
                        'Étagères murales',
                        'Armoire de salle de bain',
                        'Meuble suspendu',
                        'Autre',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${bathroomStorageType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setBathroomStorageType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Objectif principal :")}</h3>
                    <div className="request-step4-grid">
                      {['Gain d’espace', 'Organisation', 'Esthétique', 'Fonctionnalité', 'À définir'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${bathroomStorageObjective === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setBathroomStorageObjective)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isWaterHeaterService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Quelle est la capacité (en litres) du chauffe-eau ? (facultatif)")}</h3>
                    <select
                      className="request-step4-select"
                      value={waterHeaterCapacity}
                      onChange={(e) => setWaterHeaterCapacity(e.target.value)}
                    >
                      <option value="" disabled>{tw('Sélectionnez une réponse')}</option>
                      {['30L', '50L', '80L', '100L', '150L', '200L', '300L', 'Je ne sais pas'].map((option) => (
                        <option key={option} value={option}>
                          {tw(option)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("L'alimentation électrique est-elle déjà en place ?")}</h3>
                    <div className="request-step4-grid">
                      {['Oui', 'Non'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${waterHeaterElectricalReady === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setWaterHeaterElectricalReady)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("L'approvisionnement en eau est-il déjà en place ?")}</h3>
                    <div className="request-step4-grid">
                      {['Oui', 'Non', "À définir avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${waterHeaterWaterSupplyReady === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setWaterHeaterWaterSupplyReady)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Souhaitez-vous installer un thermostat ?")}</h3>
                    <div className="request-step4-grid">
                      {['Oui', 'Non'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${waterHeaterThermostatInstall === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setWaterHeaterThermostatInstall)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isMasticService ? (
                <>
                  <div>
                    <h3 className="request-step4-question">{tw("Avez-vous des exigences particulières concernant la couleur des joints ? (facultatif)")}</h3>
                    <div className="request-step4-grid">
                      {['Oui', 'Non', "À définir avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${masticColorRequirement === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setMasticColorRequirement)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isClimatisationService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("De quel type de climatiseur s’agit-il ? (facultatif)")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Unité portable',
                        'Système split',
                        'Réversible',
                        'Multisplit',
                        'Climatisation centrale',
                        'Autre',
                        "À définir avec l'artisan",
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${climatisationUnitType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setClimatisationUnitType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Y a-t-il une prise électrique à proximité ?")}</h3>
                    <div className="request-step4-grid">
                      {['Oui', 'Non'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${climatisationOutletNearby === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setClimatisationOutletNearby)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <br></br>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Faut-il installer une unité extérieure ?")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Oui',
                        'Non',
                        "À définir avec l'artisan",
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${climatisationUnitType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setClimatisationUnitType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isChauffageService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Quel type de périphérique souhaitez-vous installer ?")}</h3>
                    <div className="request-step4-assembly-list">
                      {[
                        'Radiateur électrique',
                        'Radiateur à eau chaude',
                        'Sèche-serviettes',
                        'Autre',
                        "À définir avec l'artisan",
                      ].map((option) => (
                        <div
                          key={option}
                          className={`request-step4-assembly-item ${(chauffagePeripheralCounts[option] || 0) > 0 ? 'is-active' : ''}`}
                        >
                          <span>{tw(option)}</span>
                          <div className="request-step4-assembly-controls">
                            <button
                              type="button"
                              className="request-step4-assembly-btn"
                              onClick={() => updateChauffagePeripheralCount(option, -1)}
                              disabled={(chauffagePeripheralCounts[option] || 0) === 0}
                            >
                              -
                            </button>
                            <span className="request-step4-assembly-count">{chauffagePeripheralCounts[option] || 0}</span>
                            <button
                              type="button"
                              className="request-step4-assembly-btn"
                              onClick={() => updateChauffagePeripheralCount(option, 1)}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("L’alimentation en électricité et en eau est-elle déjà disponible ? (facultatif)")}</h3>
                    <div className="request-step4-grid">
                      {['Oui', 'Non', "À définir avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${chauffageSupplyReady === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setChauffageSupplyReady)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isChaudiereService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Quel type d’énergie utilise votre chaudière ?")}</h3>
                    <div className="request-step4-grid">
                      {['Électrique', 'Bois', 'Géothermie', 'Solaire', 'Autre'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${chaudiereEnergyType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setChaudiereEnergyType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Comment utilisez-vous votre chaudière ?")}</h3>
                    <div className="request-step4-grid">
                      {['Chauffage et eau chaude', 'Chauffage uniquement', 'Eau chaude uniquement'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${chaudiereUsage === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setChaudiereUsage)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isEntretienDepannagePlumbingService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Type d’intervention :")}</h3>
                    <div className="request-step4-grid">
                      {['Entretien', 'Dépannage', 'Urgence', 'Contrôle / diagnostic'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${entretienDepannageInterventionType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setEntretienDepannageInterventionType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Équipement concerné :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Évier',
                        'Lavabo / Vasque',
                        'Douche / Baignoire',
                        'WC',
                        'Robinetterie',
                        'Tuyauterie',
                        'Chauffe-eau',
                        'Autre',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${entretienDepannageEquipment === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setEntretienDepannageEquipment)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Nature du problème :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Fuite d’eau',
                        'Canalisation bouchée',
                        'Mauvaise évacuation',
                        'Faible pression',
                        'Mauvaise odeur',
                        'Panne d’équipement',
                        'Autre',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${entretienDepannageProblemNature === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setEntretienDepannageProblemNature)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isTuyauxService ? (
                <>
                  <div>
                    <h3 className="request-step4-question">{tw("Dans quelles pièces avez-vous besoin que ces travaux soient effectués ?")}</h3>
                    <div className="request-step4-grid" style={{ gridTemplateColumns: '1fr' }}>
                      {['Cuisine', 'Salle de bain', 'Toilettes', 'Dehors', 'Sous-sol', 'Autre'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${tuyauxRooms.includes(option) ? 'is-active' : ''}`}
                          onClick={() => toggleChoice(option, setTuyauxRooms)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isKitchenPlumbingService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Type d’intervention :")}</h3>
                    <div className="request-step4-grid">
                      {['Installation', 'Réparation', 'Remplacement', 'Entretien', 'Urgence'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${kitchenPlumbingInterventionType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setKitchenPlumbingInterventionType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Équipement concerné :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Évier',
                        'Lavabo / Vasque',
                        'Douche / Baignoire',
                        'WC',
                        'Robinetterie',
                        'Tuyauterie',
                        'Chauffe-eau',
                        'Autre',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${kitchenPlumbingEquipment === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setKitchenPlumbingEquipment)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {kitchenPlumbingInterventionType === 'Réparation' && (
                    <div>
                      <h3 className="request-step4-question">{tw("Nature du problème (si réparation) :")}</h3>
                      <div className="request-step4-grid">
                        {[
                          'Fuite d’eau',
                          'Canalisation bouchée',
                          'Mauvaise évacuation',
                          'Faible pression',
                          'Mauvaise odeur',
                          'Autre',
                        ].map((option) => (
                          <button
                            key={option}
                            type="button"
                            className={`request-step4-choice ${kitchenPlumbingProblemNature === option ? 'is-active' : ''}`}
                            onClick={() => pickChoice(option, setKitchenPlumbingProblemNature)}
                          >
                            {tw(option)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : isKitchenFaucetService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Type de robinet de cuisine :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Mitigeur classique',
                        'Mitigeur avec douchette',
                        'Robinet mural',
                        'Robinet rabattable',
                        'Autre',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${kitchenFaucetType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setKitchenFaucetType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Type d’intervention :")}</h3>
                    <div className="request-step4-grid">
                      {['Installation', 'Réparation', 'Remplacement', 'Entretien'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${kitchenFaucetInterventionType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setKitchenFaucetInterventionType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {kitchenFaucetInterventionType === 'Réparation' && (
                    <div>
                      <h3 className="request-step4-question">{tw("Nature du problème (si réparation) :")}</h3>
                      <div className="request-step4-grid">
                        {[
                          'Fuite',
                          'Goutte-à-goutte',
                          'Faible pression',
                          'Robinet bloqué',
                          'Mauvaise rotation',
                          'Autre',
                        ].map((option) => (
                          <button
                            key={option}
                            type="button"
                            className={`request-step4-choice ${kitchenFaucetProblemNature === option ? 'is-active' : ''}`}
                            onClick={() => pickChoice(option, setKitchenFaucetProblemNature)}
                          >
                            {tw(option)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : isKitchenStorageService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Type de rangement :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Placards',
                        'Étagères',
                        'Tiroirs',
                        'Meubles sous évier',
                        'Meubles hauts',
                        'Garde-manger',
                        'Autre',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${kitchenStorageType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setKitchenStorageType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Objectif principal :")}</h3>
                    <div className="request-step4-grid">
                      {['Gain d’espace', 'Meilleure organisation', 'Esthétique', 'Fonctionnalité', 'À définir'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${kitchenStorageObjective === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setKitchenStorageObjective)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isBathroomService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Équipements concernés :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Lavabo / Vasque',
                        'Douche',
                        'Baignoire',
                        'WC',
                        'Robinetterie',
                        'Meuble de salle de bain',
                        'Autre',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${bathroomEquipment === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setBathroomEquipment)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Type d’intervention :")}</h3>
                    <div className="request-step4-grid">
                      {['Installation', 'Réparation', 'Remplacement', 'Rénovation complète'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${bathroomInterventionType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setBathroomInterventionType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isPortailMotoriseService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Quel type de portail souhaitez-vous ?")}</h3>
                    <div className="request-step4-size-grid">
                      {[
                        { title: 'Balançoire', image: '/images/battant.jpg' },
                        { title: 'Glissement', image: '/images/coulissant.jpg' },
                        { title: 'Cantilever', image: '/images/cantilever.jpg' },
                        { title: "À définir avec l'artisan", image: '/images/artisan.jpg' },
                      ].map((option) => (
                        <button
                          key={option.title}
                          type="button"
                          className={`request-step4-size ${portailMotoriseType === option.title ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option.title, setPortailMotoriseType)}
                        >
                          <img src={option.image} alt="" />
                          <div className="request-step4-size-label">{tw(option.title)}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Quel type de matériaux pour votre portail ?")}</h3>
                    <div className="request-step4-size-grid">
                      {[
                        { title: 'Aluminium', image: '/images/acier.jpg' },
                        { title: 'Bois', image: '/images/bois.jpg' },
                        { title: 'PVC', image: '/images/pvc.jpg' },
                        { title: 'Fer forgé', image: '/images/acier.jpg' },
                        { title: "À définir avec l'artisan", image: '/images/artisan.jpg' },
                      ].map((option) => (
                        <button
                          key={option.title}
                          type="button"
                          className={`request-step4-size ${portailMotoriseMaterial === option.title ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option.title, setPortailMotoriseMaterial)}
                        >
                          <img src={option.image} alt="" />
                          <div className="request-step4-size-label">{tw(option.title)}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Avez-vous déjà des poteaux de portail ? (facultatif)")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Non, à créer',
                        'Oui, à installer',
                        'Oui, déjà installé',
                        "À définir avec l'artisan",
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${portailMotorisePosts === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setPortailMotorisePosts)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isPortailMenuiserieService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Quel type de portail souhaitez-vous ?")}</h3>
                    <div className="request-step4-size-grid">
                      {[
                        { title: 'Portail battant', image: '/images/battant.jpg' },
                        { title: 'Portail coulissant', image: '/images/coulissant.jpg' },
                        { title: 'Portail autoportant', image: '/images/autoportant.jpg' },
                        { title: 'À définir', image: '/images/artisan.jpg' },
                      ].map((option) => (
                        <button
                          key={option.title}
                          type="button"
                          className={`request-step4-size ${portailGateType === option.title ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option.title, setPortailGateType)}
                        >
                          <img src={option.image} alt="" />
                          <div className="request-step4-size-label">{tw(option.title)}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Quel type de matériaux pour votre portail ? (facultatif)")}</h3>
                    <div className="request-step4-size-grid">
                      {[
                        { title: 'Acier', image: '/images/acier.jpg' },
                        { title: 'Bois', image: '/images/bois.jpg' },
                        { title: 'PVC', image: '/images/pvc.jpg' },
                        { title: "À définir avec l'artisan", image: '/images/artisan.jpg' },
                      ].map((option) => (
                        <button
                          key={option.title}
                          type="button"
                          className={`request-step4-size ${portailMaterial === option.title ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option.title, setPortailMaterial)}
                        >
                          <img src={option.image} alt="" />
                          <div className="request-step4-size-label">{tw(option.title)}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Avez-vous déjà des poteaux de portail ? (facultatif)")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Non, à créer',
                        'Oui, à installer',
                        'Oui, déjà installé',
                        "À définir avec l'artisan",
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${portailPosts === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setPortailPosts)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isGarageMotoriseService || isPorteGarageMenuiserieService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Quel type de matériaux pour votre porte ?")}</h3>
                    <div className="request-step4-size-grid">
                      {[
                        { title: 'Aluminium', image: '/images/acier.jpg' },
                        { title: 'Bois', image: '/images/bois.jpg' },
                        { title: 'PVC', image: '/images/pvc.jpg' },
                        { title: "À définir avec l'artisan", image: '/images/artisan.jpg' },
                      ].map((option) => (
                        <button
                          key={option.title}
                          type="button"
                          className={`request-step4-size ${porteGarageMaterial === option.title ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option.title, setPorteGarageMaterial)}
                        >
                          <img src={option.image} alt="" />
                          <div className="request-step4-size-label">{tw(option.title)}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Avez-vous besoin de planifier des travaux de soutien ?")}</h3>
                    <div className="request-step4-grid">
                      {[
                        "Agrandir l'ouverture",
                        'Réparer le cadre',
                        'Non',
                        "À définir avec l'artisan",
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${porteGarageSupportWork === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setPorteGarageSupportWork)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Faut-il enlever l'ancienne porte de garage ?")}</h3>
                    <div className="request-step4-grid">
                      {['Oui', 'Non', "À définir avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${porteGarageRemoveOld === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setPorteGarageRemoveOld)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Quel type de système de verrouillage souhaitez-vous ? (facultatif)")}</h3>
                    <div className="request-step4-grid">
                      {['Serrure à clé', 'Boulon', 'Système multipoints', "À définir avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${porteGarageLocking === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setPorteGarageLocking)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isPrisesInterrupteurService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Combien de prises / interrupteurs ?")}</h3>
                    <div className="request-step4-grid">
                      {['2 à 5', '5 à 10', '10'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${prisesInterrupteurCountRange === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setPrisesInterrupteurCountRange)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("L'installation est-elle prête ?")}</h3>
                    <div className="request-step4-grid">
                      {['Oui', 'Non', "À discuter avec l’artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${prisesInterrupteurReady === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setPrisesInterrupteurReady)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isInterphoneService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Quel système souhaitez-vous ? (facultatif)")}</h3>
                    <div className="request-step4-grid">
                      {['Interphone audio', 'Interphone audio/vidéo', 'Sonnette', 'Autre'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${interphoneSystemType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setInterphoneSystemType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Préférez-vous une installation qui soit :")}</h3>
                    <div className="request-step4-grid">
                      {['Câblé', 'Sans fil', 'Autre'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${interphoneInstallType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setInterphoneInstallType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isAlarmesSecuriteService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Lieu :")}</h3>
                    <div className="request-step4-grid">
                      {['Appartement', 'Maison', 'Commerce'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${alarmesSecuriteLieu === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setAlarmesSecuriteLieu)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Fonctionnalités :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Application mobile',
                        'Télésurveillance',
                        'Détecteurs animaux',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${alarmesSecuriteFonctionnalites === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setAlarmesSecuriteFonctionnalites)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isCamerasSecuriteService ? (
                <>
                  <div>
                    <h3 className="request-step4-question">{tw("Nombre de caméras :")}</h3>
                    <div className="request-step4-grid">
                      {['1-2', '3-6', 'Plus de 6'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${camerasSecuriteNombre === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setCamerasSecuriteNombre)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isAlarmeCameraService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Quel type d'alarme souhaitez-vous installer ?")}</h3>
                    <div className="request-step4-grid">
                      {['Câblé', 'Sans fil', 'Intelligent', 'vidéosurveillance', 'Autre', "À définir avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${alarmeType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setAlarmeType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Quels types d'éléments doivent être installés ? Précisez la quantité.")}</h3>
                    <div className="request-step4-assembly-list">
                      {[
                        'Détecteur',
                        'Panneau de contrôle',
                        'Sirène',
                        'Caméra',
                        'Alarme de piscine',
                        "À définir avec l'artisan",
                      ].map((option) => (
                        <div
                          key={option}
                          className={`request-step4-assembly-item ${(alarmeElementCounts[option] || 0) > 0 ? 'is-active' : ''}`}
                        >
                          <span>{tw(option)}</span>
                          <div className="request-step4-assembly-controls">
                            <button
                              type="button"
                              className="request-step4-assembly-btn"
                              onClick={() => updateAlarmeElementCount(option, -1)}
                              disabled={(alarmeElementCounts[option] || 0) === 0}
                            >
                              -
                            </button>
                            <span className="request-step4-assembly-value">{alarmeElementCounts[option] || 0}</span>
                            <button
                              type="button"
                              className="request-step4-assembly-btn"
                              onClick={() => updateAlarmeElementCount(option, 1)}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : isDomotiqueService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Sélectionnez le type de dispositif concerné, précisez le nombre d&apos;unités ?")}</h3>
                    <div className="request-step4-assembly-list">
                      {[
                        'Éclairage',
                        'Volet / magasin',
                        'Chauffage / climatisation',
                        'Système de sécurité',
                        'Audio / multimédia',
                        'Prise / interrupteur connecté',
                        'Interphone',
                        'Autre',
                        'Un ensemble A définir',
                      ].map((option) => (
                        <div
                          key={option}
                          className={`request-step4-assembly-item ${(domotiqueDeviceCounts[option] || 0) > 0 ? 'is-active' : ''}`}
                        >
                          <span>{tw(option)}</span>
                          <div className="request-step4-assembly-controls">
                            <button
                              type="button"
                              className="request-step4-assembly-btn"
                              onClick={() => updateDomotiqueDeviceCount(option, -1)}
                              disabled={(domotiqueDeviceCounts[option] || 0) === 0}
                            >
                              -
                            </button>
                            <span className="request-step4-assembly-value">{domotiqueDeviceCounts[option] || 0}</span>
                            <button
                              type="button"
                              className="request-step4-assembly-btn"
                              onClick={() => updateDomotiqueDeviceCount(option, 1)}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Comment souhaitez-vous contrôler le système ? (facultatif)")}</h3>
                    <div className="request-step4-grid">
                      {['application mobile', 'commande vocale', 'Télécommande', 'Autre'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${domotiqueControlMode === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setDomotiqueControlMode)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isPotagerService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Combien de plantes faut-il planter ? (facultatif)")}</h3>
                    <input
                      className="request-step4-input"
                      placeholder={tw('Exemple : 20')}
                      value={potagerPlantCount}
                      onChange={(e) => setPotagerPlantCount(e.target.value)}
                    />
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Le sol est-il prêt à être planté ?")}</h3>
                    <div className="request-step4-list">
                      {[
                        'Oui, le sol est prêt',
                        "Non, aucun travail préparatoire n'est nécessaire.",
                        "À déterminer avec l'artisan",
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${potagerSoilReady === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setPotagerSoilReady)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isFleursService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Combien de plants de fleurs planter ?")}</h3>
                    <div className="request-step4-list">
                      {[
                        '10 à 20 plants (petit espace)',
                        '20 à 50 plants (jardin moyen)',
                        '50+ plants (grand espace)',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${fleursPlantCountRange === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setFleursPlantCountRange)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Le sol est-il prêt à être planté ?")}</h3>
                    <div className="request-step4-grid">
                      {['Oui', 'Non', "À discuter avec l’artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${fleursSoilReady === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setFleursSoilReady)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isFeuillesService ? (
                <>
                  <div>
                    <h3 className="request-step4-question">{tw("Quel type de feuilles concerne votre demande ?")}</h3>
                    <div className="request-step4-grid">
                      {['Feuilles caduques', 'Feuilles persistantes'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${feuillesType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setFeuillesType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isArbustesService ? (
                <>
                  <div>
                    <h3 className="request-step4-question">{tw("Le sol est-il prêt à être planté ?")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Oui, le sol est prêt',
                        "Non, aucun travail préparatoire n'est nécessaire.",
                        "À déterminer avec l'artisan",
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${arbustesSoilReady === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setArbustesSoilReady)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isClairiereService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Hauteur approximative des arbres à planter")}</h3>
                    <select
                      className="request-step4-select"
                      value={clairiereTreeHeight}
                      onChange={(e) => setClairiereTreeHeight(e.target.value)}
                    >
                      <option value="" disabled>{tw('Sélectionnez une réponse')}</option>
                      <option value="Petit arbre">{tw('Petit arbre')}</option>
                      <option value="Moyen">{tw('Moyen')}</option>
                      <option value="Grand">{tw('Grand')}</option>
                    </select>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Le sol est-il prêt à être planté ?")}</h3>
                    <div className="request-step4-grid">
                      {['Oui', 'Non'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${clairiereSoilReady === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setClairiereSoilReady)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isArbrePlantationService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Quelle est la hauteur approximative des arbres à planter ?")}</h3>
                    <select
                      className="request-step4-select"
                      value={arbrePlantingHeight}
                      onChange={(e) => setArbrePlantingHeight(e.target.value)}
                    >
                      <option value="" disabled>{tw('Sélectionnez une réponse')}</option>
                      <option value="Moins de 1 m">{tw('Moins de 1 m')}</option>
                      <option value="Entre 1 m et 2 m">{tw('Entre 1 m et 2 m')}</option>
                      <option value="Entre 2 m et 3 m">{tw('Entre 2 m et 3 m')}</option>
                      <option value="Plus de 3 m">{tw('Plus de 3 m')}</option>
                      <option value="À définir avec l'artisan">{tw("À définir avec l'artisan")}</option>
                    </select>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Le sol est-il prêt à être planté ?")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Oui, le sol est prêt',
                        "Non, aucun travail préparatoire n'est nécessaire.",
                        "À déterminer avec l'artisan",
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${arbreSoilReady === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setArbreSoilReady)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isAbattageArbresService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Nombre d'arbres concernés ?")}</h3>
                    <input
                      className="request-step4-input"
                      placeholder={tw('exemple : 2')}
                      value={abattageTreeCount}
                      onChange={(e) => setAbattageTreeCount(e.target.value)}
                    />
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Quelle est la hauteur des arbres ?")}</h3>
                    <select
                      className="request-step4-select"
                      value={abattageTreeHeight}
                      onChange={(e) => setAbattageTreeHeight(e.target.value)}
                    >
                      <option value="" disabled>{tw('Sélectionnez une réponse')}</option>
                      <option value="Moins de 3 m">{tw('Moins de 3 m')}</option>
                      <option value="Entre 3 m et 6 m">{tw('Entre 3 m et 6 m')}</option>
                      <option value="Entre 6 m et 10 m">{tw('Entre 6 m et 10 m')}</option>
                      <option value="Plus de 10 m">{tw('Plus de 10 m')}</option>
                      <option value="À définir avec l'artisan">{tw("À définir avec l'artisan")}</option>
                    </select>
                  </div>
                </>
              ) : isPelouseService ? (
                <>
                  <div>
                    <h3 className="request-step4-question">{tw("Le sol a-t-il besoin de terre végétale ou d'autres amendements ?")}</h3>
                    <div className="request-step4-grid">
                      {['Oui', 'Non', "À définir avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${pelouseSoilAmendments === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setPelouseSoilAmendments)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isHaiesService ? (
                <>
                  <div>
                    <h3 className="request-step4-question">{tw("Disposez-vous d'outils sur place pour la taille ?")}</h3>
                    <div className="request-step4-list">
                      {[
                        'Oui, je dispose du matériel nécessaire',
                        "Non, c'est à l'artisan d'apporter l'équipement.",
                        "À définir avec l'artisan",
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${haiesTools === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setHaiesTools)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isTondreService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Disposez-vous d'outils sur place pour tondre la pelouse ?")}</h3>
                    <div className="request-step4-list">
                      {[
                        'Oui, je dispose du matériel nécessaire',
                        "Non, les artisans doivent venir avec l'équipement.",
                        "À définir avec l'artisan",
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${tondreTools === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setTondreTools)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Souhaitez-vous que l'herbe coupée soit enlevée ?")}</h3>
                    <div className="request-step4-grid">
                      {['Oui', 'Non', "À définir avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${tondreGrassRemoval === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setTondreGrassRemoval)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isEspaceVertsService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Disposez-vous d'outils sur place pour la maintenance ?")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Oui, je dispose du matériel nécessaire',
                        "Non, c'est à l'artisan d'apporter l'équipement.",
                        "À discuter avec l'artisan",
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${espaceVertsTools === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setEspaceVertsTools)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Un entretien régulier est-il nécessaire ?")}</h3>
                    <div className="request-step4-list">
                      {['Oui', 'Non'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${espaceVertsRegularMaintenance === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setEspaceVertsRegularMaintenance)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isOuverturePorteService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Type de porte :")}</h3>
                    <div className="request-step4-grid">
                      {['Porte standard', 'Porte blindée', 'Porte vitrée'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${ouverturePorteTypePorte === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setOuverturePorteTypePorte)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Problème rencontré :")}</h3>
                    <div className="request-step4-grid">
                      {['Clé perdue', 'Clé cassée', 'Porte claquée', 'Serrure bloquée'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${ouverturePorteProblemeRencontre === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setOuverturePorteProblemeRencontre)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isPortesSecuriseesService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Type de porte :")}</h3>
                    <div className="request-step4-grid">
                      {['Porte appartement', 'Porte maison', 'Porte palière'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${portesSecuriseesTypePorte === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setPortesSecuriseesTypePorte)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Serrure :")}</h3>
                    <div className="request-step4-grid">
                      {['3 points', '5 points', 'Multipoints'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${portesSecuriseesSerrure === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setPortesSecuriseesSerrure)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isSerrurerieService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Type de porte / serrure :")}</h3>
                    <div className="request-step4-grid">
                      {['Appartement', 'Maison', 'Garage', 'Voiture'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${serrurerieTypePorteSerrure === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setSerrurerieTypePorteSerrure)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Problème :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Clé perdue',
                        'Clé cassée',
                        'Serrure bloquée',
                        'Porte claquée',
                        'À discuter',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${serrurerieProbleme === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setSerrurerieProbleme)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isEntretienReparationMecaniqueService ? (
                <>
                  <div>
                    <h3 className="request-step4-question">{tw("Prestation principale :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Vidange',
                        'Freins',
                        'Embrayage',
                        'Distribution',
                        'Climatisation',
                        'Diagnostic',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${mecaniqueEntretienPrestationPrincipale === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setMecaniqueEntretienPrestationPrincipale)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isChangementRouesMecaniqueService ? (
                <>
                  <div>
                    <h3 className="request-step4-question">{tw("Nombre de roues :")}</h3>
                    <div className="request-step4-grid">
                      {['1', '2', '4'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${mecaniqueChangementRouesNombre === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setMecaniqueChangementRouesNombre)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isBatterieElectriqueMecaniqueService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Véhicule :")}</h3>
                    <div className="request-step4-grid">
                      {['Thermique', 'Électrique', 'Hybride'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${mecaniqueBatterieVehicule === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setMecaniqueBatterieVehicule)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Symptôme :")}</h3>
                    <div className="request-step4-grid">
                      {['Ne démarre pas', 'Voyant allumé', 'Perte d’autonomie'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${mecaniqueBatterieSymptome === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setMecaniqueBatterieSymptome)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Services additionnels :")}</h3>
                    <div className="request-step4-grid request-step4-grid--services-one-line">
                      {[
                        'Programmation',
                        'Recharge sur place',
                        "À discuter avec l'artisan",
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${mecaniqueBatterieServicesAdditionnels === option ? 'is-active' : ''}`}
                          onClick={() =>
                            pickChoice(option, setMecaniqueBatterieServicesAdditionnels)
                          }
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isPlaquetteFreinMecaniqueService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Symptôme :")}</h3>
                    <div className="request-step4-grid">
                      {['Bruit / Grincement', 'Usure normale', 'Pédale molle'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${mecaniquePlaquetteFreinSymptome === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setMecaniquePlaquetteFreinSymptome)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Services additionnels :")}</h3>
                    <div className="request-step4-grid request-step4-grid--services-one-line">
                      {[
                        'Disques de frein',
                        'Liquide de frein',
                        'Contrôle étriers',
                        "À discuter avec l'artisan",
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${mecaniquePlaquetteFreinServicesAdditionnels === option ? 'is-active' : ''}`}
                          onClick={() =>
                            pickChoice(option, setMecaniquePlaquetteFreinServicesAdditionnels)
                          }
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isDepannageRemorquageMecaniqueService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Type de panne :")}</h3>
                    <div className="request-step4-grid request-step4-grid--panne-ligne1">
                      {['Batterie', 'Crevaison', 'Moteur', 'Accident'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${mecaniqueDepannageTypePanne === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setMecaniqueDepannageTypePanne)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                    <div className="request-step4-grid request-step4-grid--panne-ligne2">
                      {['Carburant', 'Clé perdue', 'À discuter'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${mecaniqueDepannageTypePanne === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setMecaniqueDepannageTypePanne)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Urgence :")}</h3>
                    <div className="request-step4-grid">
                      {['Immédiate', "Aujourd'hui", 'À convenir'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${mecaniqueDepannageUrgence === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setMecaniqueDepannageUrgence)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Destination du remorquage :")}</h3>
                    <div className="request-step4-grid">
                      {['Garage', 'Domicile', "À discuter avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${mecaniqueDepannageDestinationRemorquage === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setMecaniqueDepannageDestinationRemorquage)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isChangementPiecesAutoMecaniqueService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Pièce concernée :")}</h3>
                    <div className="request-step4-grid request-step4-grid--panne-ligne1">
                      {['Freins', 'Batterie', 'Embrayage', 'Distribution'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${mecaniquePiecesAutoPieceConcernee === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setMecaniquePiecesAutoPieceConcernee)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                    <div className="request-step4-grid request-step4-grid--panne-ligne2">
                      {['Amortisseurs', 'Phares', 'Autre'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${mecaniquePiecesAutoPieceConcernee === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setMecaniquePiecesAutoPieceConcernee)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isDiagnosticNettoyageMecaniqueService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Problème principal :")}</h3>
                    <div className="request-step4-grid request-step4-grid--diag-probleme-ligne1">
                      {['Voyant allumé', 'Perte de puissance', 'Climatisation faible'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${mecaniqueDiagnosticNettoyageProblemePrincipal === option ? 'is-active' : ''}`}
                          onClick={() =>
                            pickChoice(option, setMecaniqueDiagnosticNettoyageProblemePrincipal)
                          }
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                    <div className="request-step4-grid request-step4-grid--diag-probleme-ligne2">
                      {['Intérieur sale', 'À discuter'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${mecaniqueDiagnosticNettoyageProblemePrincipal === option ? 'is-active' : ''}`}
                          onClick={() =>
                            pickChoice(option, setMecaniqueDiagnosticNettoyageProblemePrincipal)
                          }
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isPorteMenuiserieService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Type de porte :")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Porte intérieure',
                        'Porte d’entrée',
                        'Porte coulissante',
                        'Porte vitrée',
                        'Porte blindée',
                        'Autre',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${porteDoorType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setPorteDoorType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Type d’intervention :")}</h3>
                    <div className="request-step4-grid">
                      {['Installation', 'Réparation', 'Remplacement', 'Réglage'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${porteInterventionDetail === option ? 'is-active' : ''}`}
                          onClick={() => {
                            pickChoice(option, setPorteInterventionDetail);
                            if (option !== 'Réparation') setPorteProblemNature('');
                          }}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {porteInterventionDetail === 'Réparation' && (
                    <div className="mb-5">
                      <h3 className="request-step4-question">{tw("Nature du problème (si réparation) :")}</h3>
                      <div className="request-step4-grid">
                        {[
                          'Porte qui ne ferme pas',
                          'Porte bloquée',
                          'Charnière abîmée',
                          'Poignée cassée',
                          'Serrure défectueuse',
                          'Autre',
                        ].map((option) => (
                          <button
                            key={option}
                            type="button"
                            className={`request-step4-choice ${porteProblemNature === option ? 'is-active' : ''}`}
                            onClick={() => pickChoice(option, setPorteProblemNature)}
                          >
                            {tw(option)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <h3 className="request-step4-question">{tw("Matériau de la porte :")}</h3>
                    <div className="request-step4-grid">
                      {['Bois', 'Métal', 'PVC', 'Verre', 'Mixte', 'Je ne sais pas'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${porteMaterial === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setPorteMaterial)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isFenetresPortesFenetresService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("De quel type de matériau s&apos;agit-il ?")}</h3>
                    <div className="request-step4-size-grid">
                      {[
                        { title: 'Aluminium', image: '/images/acier.jpg' },
                        { title: 'Bois', image: '/images/bois.jpg' },
                        { title: 'PVC', image: '/images/pvc.jpg' },
                        { title: 'À discuter avec l’artisan', image: '/images/artisan.jpg' },
                      ].map((option) => (
                        <button
                          key={option.title}
                          type="button"
                          className={`request-step4-size ${fenetresFrameMaterial === option.title ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option.title, setFenetresFrameMaterial)}
                        >
                          <img src={option.image} alt="" />
                          <div className="request-step4-size-label">{tw(option.title)}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Quel type de fenêtre souhaitez-vous ?")}</h3>
                    <div className="request-step4-grid" style={{ gridTemplateColumns: '1fr' }}>
                      {[
                        'Fenêtre à un seul battant',
                        'Fenêtre à double battant',
                        'Inclinable et pivotant',
                        'Porte-fenêtre',
                        'Glissement',
                        'Lucarne',
                        'Fixé',
                        'Autre',
                        "À définir avec l'artisan",
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${fenetresWindowType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setFenetresWindowType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isVestiaireMenuiserieService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Sélectionnez le type de porte à installer et indiquez la quantité.")}</h3>
                    <div className="request-step4-assembly-list">
                      {[
                        'Glissement',
                        'Charnière',
                        'Porte avec miroir intégré',
                        'Porte vitrée',
                        'Rideaux',
                        'Pas de porte / Autre',
                        "À définir avec l'artisan",
                      ].map((option) => (
                        <div
                          key={option}
                          className={`request-step4-assembly-item ${(vestiaireDoorCounts[option] || 0) > 0 ? 'is-active' : ''}`}
                        >
                          <span>{tw(option)}</span>
                          <div className="request-step4-assembly-controls">
                            <button
                              type="button"
                              className="request-step4-assembly-btn"
                              onClick={() => updateVestiaireDoorCount(option, -1)}
                              disabled={(vestiaireDoorCounts[option] || 0) === 0}
                            >
                              -
                            </button>
                            <span className="request-step4-assembly-count">{vestiaireDoorCounts[option] || 0}</span>
                            <button
                              type="button"
                              className="request-step4-assembly-btn"
                              onClick={() => updateVestiaireDoorCount(option, 1)}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Sélectionnez les éléments à installer et spécifiez la quantité ?")}</h3>
                    <div className="request-step4-assembly-list">
                      {['Tiroir', 'Étagère', 'Tringle à vêtements', 'Plateau coulissant', 'Autre'].map((option) => (
                        <div
                          key={option}
                          className={`request-step4-assembly-item ${(vestiaireInteriorCounts[option] || 0) > 0 ? 'is-active' : ''}`}
                        >
                          <span>{tw(option)}</span>
                          <div className="request-step4-assembly-controls">
                            <button
                              type="button"
                              className="request-step4-assembly-btn"
                              onClick={() => updateVestiaireInteriorCount(option, -1)}
                              disabled={(vestiaireInteriorCounts[option] || 0) === 0}
                            >
                              -
                            </button>
                            <span className="request-step4-assembly-count">{vestiaireInteriorCounts[option] || 0}</span>
                            <button
                              type="button"
                              className="request-step4-assembly-btn"
                              onClick={() => updateVestiaireInteriorCount(option, 1)}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : isCuisineMenuiserieService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Précisez les armoires à installer et leur quantité.")}</h3>
                    <div className="request-step4-assembly-list">
                      {['Armoires murales', 'Meubles bas', 'Colonne de cabinet'].map((option) => (
                        <div
                          key={option}
                          className={`request-step4-assembly-item ${(menuiserieCuisineArmoiresCounts[option] || 0) > 0 ? 'is-active' : ''}`}
                        >
                          <span>{tw(option)}</span>
                          <div className="request-step4-assembly-controls">
                            <button
                              type="button"
                              className="request-step4-assembly-btn"
                              onClick={() => updateMenuiserieCuisineArmoiresCount(option, -1)}
                              disabled={(menuiserieCuisineArmoiresCounts[option] || 0) === 0}
                            >
                              -
                            </button>
                            <span className="request-step4-assembly-count">
                              {menuiserieCuisineArmoiresCounts[option] || 0}
                            </span>
                            <button
                              type="button"
                              className="request-step4-assembly-btn"
                              onClick={() => updateMenuiserieCuisineArmoiresCount(option, 1)}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Combien de mètres linéaires pour votre cuisine ? (facultatif)")}</h3>
                    <input
                      type="text"
                      className="request-step4-input"
                      placeholder={tw('mètres linéaires pour votre cuisine (ex. : 3)')}
                      value={menuiserieCuisineLinearMeters}
                      onChange={(e) => setMenuiserieCuisineLinearMeters(e.target.value)}
                    />
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Quelle est la configuration de votre cuisine ? (facultatif)")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'En forme de I',
                        'En forme de L',
                        'En forme de U',
                        'En forme de II',
                        'Avec une île',
                        'Autre',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${menuiserieCuisineLayout === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setMenuiserieCuisineLayout)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                    
                  </div>
                  <br></br>
                  <div>
                    <h3 className="request-step4-question">{tw("Souhaitez-vous installer un appareil électroménager ?")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Oui',
                        'Non',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${menuiserieCuisineLayout === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setMenuiserieCuisineLayout)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                    
                  </div>
                  
                </>
              ) : isStoreMotoriseService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Combien de stores sont concernés ?")}</h3>
                    <input
                      className="request-step4-input"
                      placeholder={tw('Nombre de stores')}
                      value={storeMotoriseCount}
                      onChange={(e) => setStoreMotoriseCount(e.target.value)}
                    />
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("De quels matériaux sont faits vos stores ?")}</h3>
                    <div className="request-step4-size-grid request-step4-size-grid--store-motorise">
                      {[
                        { title: 'Aluminium / métal', image: '/images/acier.jpg' },
                        { title: 'Bois', image: '/images/bois.jpg' },
                        { title: 'PVC', image: '/images/pvc.jpg' },
                        { title: 'Autre' },
                        { title: "À définir avec l'artisan", image: '/images/artisan.jpg' },
                      ].map((option) => (
                        <button
                          key={option.title}
                          type="button"
                          className={`request-step4-size ${storeMotoriseMaterial === option.title ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option.title, setStoreMotoriseMaterial)}
                        >
                          {option.image ? (
                            <img src={option.image} alt={tw(option.title)} />
                          ) : (
                            <div className="request-step4-icon-only" aria-hidden>
                              ...
                            </div>
                          )}
                          <div className="request-step4-size-label">{tw(option.title)}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("L'électricité est-elle déjà disponible à proximité ?")}</h3>
                    <div className="request-step4-grid">
                      {['Oui', 'Non, il faut tirer un câble.', "À définir avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${storeMotoriseElectricityReady === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setStoreMotoriseElectricityReady)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isStoresMenuiserieService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Type de store, précisez la quantité")}</h3>
                    <div className="request-step4-assembly-list">
                      {['Vénitien', 'Rouleau', 'Romain', 'Autre', "À définir avec l'artisan"].map((option) => (
                        <div
                          key={option}
                          className={`request-step4-assembly-item ${(storesBlindCounts[option] || 0) > 0 ? 'is-active' : ''}`}
                        >
                          <span>{tw(option)}</span>
                          <div className="request-step4-assembly-controls">
                            <button
                              type="button"
                              className="request-step4-assembly-btn"
                              onClick={() => updateStoresBlindCount(option, -1)}
                              disabled={(storesBlindCounts[option] || 0) === 0}
                            >
                              -
                            </button>
                            <span className="request-step4-assembly-count">{storesBlindCounts[option] || 0}</span>
                            <button
                              type="button"
                              className="request-step4-assembly-btn"
                              onClick={() => updateStoresBlindCount(option, 1)}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Quel type de mur avez-vous ?")}</h3>
                    <div className="request-step4-size-grid">
                      {[
                        { title: 'Béton', image: '/images/beton2.webp' },
                        { title: 'Brique', image: '/images/brique.jpg' },
                        { title: 'Bois', image: '/images/bois.jpg' },
                        { title: 'Cloisons sèches', image: '/images/cloisons.jpg' },
                        { title: 'Tuile', image: '/images/tuile.jpg' },
                        { title: "À définir avec l'artisan", image: '/images/artisan.jpg' },
                      ].map((option) => (
                        <button
                          key={option.title}
                          type="button"
                          className={`request-step4-size ${storesWallType === option.title ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option.title, setStoresWallType)}
                        >
                          <img src={option.image} alt={tw(option.title)} />
                          <div className="request-step4-size-label">{tw(option.title)}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isCurtainRodService ? (
                <>
                  <div>
                    <h3 className="request-step4-question">{tw("Quel type de mur ?")}</h3>
                    <div className="request-step4-size-grid">
                      {[
                        { title: 'Béton', image: '/images/beton2.webp' },
                        { title: 'Brique', image: '/images/brique.jpg' },
                        { title: 'Plaque de plâtre', image: '/images/platre.jpg' },
                        { title: 'Bois', image: '/images/bois.jpg' },
                        { title: 'Tuile', image: '/images/tuile.jpg' },
                        { title: "À définir avec l'artisan", image: '/images/artisan.jpg' },
                      ].map((option) => (
                        <button
                          key={option.title}
                          type="button"
                          className={`request-step4-size ${curtainRodWallType === option.title ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option.title, setCurtainRodWallType)}
                        >
                          <img src={option.image} alt={tw(option.title)} />
                          <div className="request-step4-size-label">{tw(option.title)}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isShelfService ? (
                <>
                  <div>
                    <h3 className="request-step4-question">{tw("Quelle est la nature du mur ? (facultatif)")}</h3>
                    <div className="request-step4-size-grid">
                      {[
                        { title: 'Béton', image: '/images/beton2.webp' },
                        { title: 'Brique', image: '/images/brique.jpg' },
                        { title: 'Bois', image: '/images/bois.jpg' },
                        { title: 'cloisons sèches', image: '/images/cloisons.jpg' },
                        { title: 'Carrelage', image: '/images/tuile.jpg' },
                        { title: 'À définir ensemble', image: '/images/artisan.jpg' },
                      ].map((option) => (
                        <button
                          key={option.title}
                          type="button"
                          className={`request-step4-size ${shelfWallType === option.title ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option.title, setShelfWallType)}
                        >
                          <img src={option.image} alt={tw(option.title)} />
                          <div className="request-step4-size-label">{tw(option.title)}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isFixationMuralService ? (
                <>
                  <div>
                    <h3 className="request-step4-question">{tw("De quel type de mur s'agit-il ?")}</h3>
                    <div className="request-step4-size-grid">
                      {[
                        { title: 'Béton', image: '/images/beton2.webp' },
                        { title: 'Brique', image: '/images/ceramique.jpg' },
                        { title: 'Bois', image: '/images/bois.jpg' },
                        { title: 'cloisons sèches', image: '/images/pvc.jpg' },
                        { title: 'Tuile', image: '/images/tuile.jpg' },
                        { title: "À définir avec l'artisan", image: '/images/artisan.jpg' },
                      ].map((option) => (
                        <button
                          key={option.title}
                          type="button"
                          className={`request-step4-size ${fixationWallType === option.title ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option.title, setFixationWallType)}
                        >
                          <img src={option.image} alt={tw(option.title)} />
                          <div className="request-step4-size-label">{tw(option.title)}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isAssemblyService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Vos meubles sont-ils :")}</h3>
                    <div className="request-step4-grid">
                      {['Neufs (en kit)', 'Déjà montés', 'À démonter puis remonter', 'Occasion', 'Autre'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${assemblyFurnitureCondition === option ? 'is-active' : ''}`}
                          onClick={() => setAssemblyFurnitureCondition(option)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Type de meubles :")}</h3>
                    <div className="request-step4-assembly-list">
                      {[
                        'Armoire / Dressing',
                        'Lit / Sommier',
                        'Table / Bureau',
                        'Canapé',
                        'Meuble TV',
                        'Cuisine',
                        'Autre',
                      ].map((option) => (
                        <div
                          key={option}
                          className={`request-step4-assembly-item ${(assemblyFurnitureCounts[option] || 0) > 0 ? 'is-active' : ''}`}
                        >
                          <span>{tw(option)}</span>
                          <div className="request-step4-assembly-controls">
                            <button
                              type="button"
                              className="request-step4-assembly-btn"
                              onClick={() => updateAssemblyCount(option, -1)}
                              disabled={(assemblyFurnitureCounts[option] || 0) === 0}
                            >
                              -
                            </button>
                            <span className="request-step4-assembly-count">{assemblyFurnitureCounts[option] || 0}</span>
                            <button
                              type="button"
                              className="request-step4-assembly-btn"
                              onClick={() => updateAssemblyCount(option, 1)}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Faut-il fixer des éléments au mur ?")}</h3>
                    <div className="request-step4-grid">
                      {['Oui', 'Non'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${assemblyWallFixRequired === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setAssemblyWallFixRequired)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {assemblyWallFixRequired === 'Oui' && (
                    <div>
                      <h4 className="request-step4-question">{tw('Sur quel type de mur les meubles seront-ils fixés ?')}</h4>
                      <div className="request-step4-size-grid">
                        {[
                          { title: 'Béton', image: '/images/beton2.webp' },
                          { title: 'Brique', image: '/images/ceramique.jpg' },
                          { title: 'Bois', image: '/images/bois.jpg' },
                          { title: 'cloisons sèches', image: '/images/pvc.jpg' },
                          { title: 'Tuile', image: '/images/tuile.jpg' },
                          { title: 'Autre', image: '/images/artisan.jpg' },
                          { title: "À définir avec l'artisan", image: '/images/artisan.jpg' },
                        ].map((option) => (
                          <button
                            key={option.title}
                            type="button"
                            className={`request-step4-size ${assemblyWallType === option.title ? 'is-active' : ''}`}
                            onClick={() => pickChoice(option.title, setAssemblyWallType)}
                          >
                            {option.image ? (
                              <img src={option.image} alt={tw(option.title)} />
                            ) : (
                              <div className="request-step4-icon-only">
                                <div className="request-step4-size-label">...</div>
                              </div>
                            )}
                            <div className="request-step4-size-label">{tw(option.title)}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : isCarpetService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Quel type de revêtement de sol souhaitez-vous installer ?")}</h3>
                    <div className="request-step4-grid">
                      {['Carrelage', 'Rouleaux', 'Planches', 'Autre'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${carpetInstallType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setCarpetInstallType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("La pièce est-elle complètement vide de meubles ?")}</h3>
                    <div className="request-step4-grid">
                      {['Oui', 'Non', "À discuter avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${carpetRoomEmpty === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setCarpetRoomEmpty)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isPlasterboardService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Quelle est la surface concernée en m² ? (facultatif)")}</h3>
                    <input
                      className="request-step4-input"
                      placeholder={tw('Exemple : 10')}
                      value={coveredSurface}
                      onChange={(e) => setCoveredSurface(e.target.value)}
                    />
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Quel type de finition souhaitez-vous ? (facultatif)")}</h3>
                    <div className="request-step4-grid">
                      {[
                        'Peinture',
                        'Papier peint',
                        'Plâtre ou stuc',
                        'Plâtre décoratif',
                        'Panneaux en bois',
                        'Carrelage',
                        'Béton ciré',
                        'Autre',
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${finishType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setFinishType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isPlasterService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Quelle est la surface murale à enduire, en m² ? (facultatif)")}</h3>
                    <input
                      className="request-step4-input"
                      placeholder={tw('Exemple : 10')}
                      value={wallPlasterSurface}
                      onChange={(e) => setWallPlasterSurface(e.target.value)}
                    />
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Quelle est la surface du plafond à enduire, en m² ? (facultatif)")}</h3>
                    <input
                      className="request-step4-input"
                      placeholder={tw('Exemple : 8')}
                      value={ceilingPlasterSurface}
                      onChange={(e) => setCeilingPlasterSurface(e.target.value)}
                    />
                  </div>
                </>
              ) : isParquetService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Combien de pièces sont concernées ?")}</h3>
                    <select
                      className="request-step4-select"
                      value={bedroomCount}
                      onChange={(e) => setBedroomCount(e.target.value)}
                    >
                      <option value="" disabled>{tw('Sélectionnez une réponse')}</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5">5</option>
                      <option value="6">6</option>
                      <option value="7">7</option>
                      <option value="8">8</option>
                      <option value="9">9</option>
                      <option value="10">10</option>
                      <option value="Plus de 10 pièces">{tw('Plus de 10 pièces')}</option>
                    </select>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Quel type d'installation souhaitez-vous ?")}</h3>
                    <div className="request-step4-grid">
                      {['Flottant', 'Collé', 'Cloué', "À définir avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${installationType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setInstallationType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mt-5 mb-5">
                    <h3 className="request-step4-question">{tw("Souhaitez-vous une sous-couche ? (facultatif)")}</h3>
                    <div className="request-step4-grid">
                      {['Oui', 'Non', "À définir avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${parquetUnderlay === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setParquetUnderlay)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Quel type de revêtement de sol est actuellement installé ?")}</h3>
                    <div className="request-step4-size-grid">
                      {[
                        { title: 'Béton', image: '/images/beton2.webp' },
                        { title: 'Tuile', image: '/images/tuile.jpg' },
                        { title: 'Bois dur', image: '/images/bois.jpg' },
                        { title: 'Vinyle', image: '/images/vinyle.jpg' },
                        { title: 'Tapis', image: '/images/tapis2.jpg' },
                        { title: "À définir avec l'artisan", image: '/images/artisan.jpg' },
                      ].map((option) => (
                        <button
                          key={option.title}
                          type="button"
                          className={`request-step4-size ${parquetExistingFloorType === option.title ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option.title, setParquetExistingFloorType)}
                        >
                          <img src={option.image} alt={tw(option.title)} />
                          <div className="request-step4-size-label">{tw(option.title)}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isVoletMotoriseService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Combien de volets sont concernés ?")}</h3>
                    <input
                      className="request-step4-input"
                      value={voletMotoriseCount}
                      onChange={(e) => setVoletMotoriseCount(e.target.value)}
                    />
                  </div>

              

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Quel type de matériaux pour vos volets ?")}</h3>
                    <div className="request-step4-size-grid">
                      {[
                        { title: 'Aluminium', image: '/images/acier.jpg' },
                        { title: 'Bois', image: '/images/bois.jpg' },
                        { title: 'PVC', image: '/images/pvc.jpg' },
                        { title: "À définir avec l'artisan", image: '/images/artisan.jpg' },
                      ].map((option) => (
                        <button
                          key={option.title}
                          type="button"
                          className={`request-step4-size ${voletMotoriseMaterial === option.title ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option.title, setVoletMotoriseMaterial)}
                        >
                          <img src={option.image} alt={tw(option.title)} />
                          <div className="request-step4-size-label">{tw(option.title)}</div>
                        </button>
                      ))}
                    </div>
                  </div>


                </>
              ) : isObturateurMenuiserieService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Combien de volets sont concernés ?")}</h3>
                    <input
                      className="request-step4-input"
                      value={obturateurCount}
                      onChange={(e) => setObturateurCount(e.target.value)}
                    />
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Quel type de volets avez-vous ?")}</h3>
                    <div className="request-step4-grid" style={{ gridTemplateColumns: '1fr' }}>
                      {[
                        'Charnière',
                        'Glissement',
                        'Rouleau',
                        'À persiennes',
                        'Pliant',
                        'Autre',
                        "À définir avec l'artisan",
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${obturateurType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setObturateurType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Quel type de matériaux pour vos volets ?")}</h3>
                    <div className="request-step4-size-grid">
                      {[
                        { title: 'Aluminium', image: '/images/acier.jpg' },
                        { title: 'Bois', image: '/images/bois.jpg' },
                        { title: 'PVC', image: '/images/pvc.jpg' },
                        { title: "À définir avec l'artisan", image: '/images/artisan.jpg' },
                      ].map((option) => (
                        <button
                          key={option.title}
                          type="button"
                          className={`request-step4-size ${obturateurMaterial === option.title ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option.title, setObturateurMaterial)}
                        >
                          <img src={option.image} alt={tw(option.title)} />
                          <div className="request-step4-size-label">{tw(option.title)}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Avez-vous besoin de prévoir des travaux de maçonnerie ?")}</h3>
                    <div className="request-step4-grid">
                      {['Oui', 'Non', "À définir avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${obturateurMasonryWork === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setObturateurMasonryWork)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isSkirtingService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("De quel type de plinthe s'agit-il ?")}</h3>
                    <div className="request-step4-size-grid">
                      {[
                        { title: 'Bois', image: '/images/bois.jpg' },
                        { title: 'PVC', image: '/images/pvc.jpg' },
                        { title: 'MDF', image: '/images/mdf.jpg' },
                        { title: 'Tuile', image: '/images/tuile.jpg' },
                        { title: "À définir avec l'artisan", image: '/images/artisan.jpg' },
                      ].map((option) => (
                        <button
                          key={option.title}
                          type="button"
                          className={`request-step4-size ${baseboardType === option.title ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option.title, setBaseboardType)}
                        >
                          {option.image ? (
                            <img src={option.image} alt={tw(option.title)} />
                          ) : (
                            <div className="request-step4-icon-only">
                              <div className="request-step4-size-label">...</div>
                            </div>
                          )}
                          <div className="request-step4-size-label">{tw(option.title)}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Souhaiteriez-vous que les plinthes soient")}</h3>
                    <div className="request-step4-grid">
                      {['Inachevé', 'Peint', 'Autre', "À définir avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${baseboardFinish === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setBaseboardFinish)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
          

                  <div>
                    <h3 className="request-step4-question">{tw("Combien de mètres linéaires sont concernés ? (Facultatif)")}</h3>
                    <input
                      className="request-step4-input"
                      placeholder={tw('Exemple : 3')}
                      value={baseboardLength}
                      onChange={(e) => setBaseboardLength(e.target.value)}
                    />
                  </div>
                  <br></br> 
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("S'agit-il des murs sur lesquels les plinthes seront installées ? ")}</h3>
                    <div className="request-step4-grid">
                      {['En bon état', 'Préparation nécessaire', "À définir avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${baseboardFinish === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setBaseboardFinish)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <br></br>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Comment souhaitez-vous fixer vos plinthes ? ")}</h3>
                    <div className="request-step4-grid">
                      {['Colle', 'Vis', 'Autre'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${baseboardFinish === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setBaseboardFinish)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isWallpaperService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Quelle est la longueur du mur ou de la surface concernée ? (facultatif)")}</h3>
                    <input
                      className="request-step4-input"
                      placeholder={tw('Exemple : 20')}
                      value={wallLength}
                      onChange={(e) => setWallLength(e.target.value)}
                    />
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Quelle est la hauteur du mur ou de la surface concernée ? (facultatif)")}</h3>
                    <div className="request-step4-grid">
                      {['Standard', 'Particulièrement élevé', 'Particulièrement bas', "À déterminer avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${wallHeight === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setWallHeight)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Votre revêtement mural est-il")}</h3>
                    <div className="request-step4-grid">
                      {['à motifs', 'Texturé', 'Très épais', 'Plaine', "À déterminer avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${wallCoverType === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setWallCoverType)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : isPaintingService ? (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Le revêtement mural doit-il être retiré ?")}</h3>
                    <div className="request-step4-grid">
                      {['Oui', 'Non', 'À définir ensemble'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${wallCoverRemoval === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setWallCoverRemoval)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Quel est l'état de la surface à peindre ? (facultatif)")}</h3>
                    <div className="request-step4-grid">
                      {['Bon état', 'État moyen', 'Mauvais état'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${paintSurfaceState === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setPaintSurfaceState)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Certaines zones doivent-elles être protégées avant d'être peintes ?")}</h3>
                    <div className="d-grid gap-2">
                      {[
                        'Oui, tout (sol, meubles, etc.)',
                        'Oui, le sol',
                        'Oui, les bords de certains éléments (par exemple les prises, les portes,...)',
                        "Non, je m'en occuperai moi-même.",
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${protectionAreas === option ? 'is-active' : ''}`}
                          onClick={() => pickChoice(option, setProtectionAreas)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Vos carreaux sont-ils là ?")}</h3>
                    <div className="request-step4-grid">
                      {['Plaine', 'à motifs', 'Texturé', 'Très épais', 'Autre'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${tileFinish.includes(option) ? 'is-active' : ''}`}
                          onClick={() => toggleChoice(option, setTileFinish)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="request-step4-question">{tw("Vos carreaux sont :")}</h3>
                    <div className="request-step4-size-grid">
                      {sizeCards.map((card) => (
                        <button
                          key={card.title}
                          type="button"
                          className={`request-step4-size ${tileSize.includes(card.title) ? 'is-active' : ''}`}
                          onClick={() => toggleChoice(card.title, setTileSize)}
                        >
                          <img src={card.image} alt={card.title} />
                          <div className="request-step4-size-label">{card.title}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="request-step4-question">{tw("Quel est l'état du sol/mur ?")}</h3>
                    <div className="request-step4-grid">
                      {['Prêt à carreler', 'Fissuré', 'Inégal', "À définir avec l'artisan"].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`request-step4-choice ${surfaceState.includes(option) ? 'is-active' : ''}`}
                          onClick={() => toggleChoice(option, setSurfaceState)}
                        >
                          {tw(option)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="request-step4-footer">
              <NioButton
                className="btn btn-primary"
                label={t('common.back')}
                onClick={() => navigate(-1)}
              />
              <NioButton
                className={`btn ${canContinue ? 'btn-primary' : 'btn-primary disabled'}`}
                label={t('common.next')}
                onClick={() => {
                  if (!canContinue) return;
                  const firstImageFile = uploadedFiles.find((file) => file.type?.startsWith('image/'));
                  const selectedImageUrl = firstImageFile ? URL.createObjectURL(firstImageFile) : '';
                  navigate(`/service/${serviceId}/${usesDocumentStepAtStep4 ? 'request-demand-step6' : 'request-demand-step5'}`, {
                    state: {
                      selectedSubService,
                      parentServiceTitle,
                      projectTitle,
                      isStep5Skipped: usesDocumentStepAtStep4,
                      requestDetails,
                      requestStep4Details: isOutdoorPavingService
                        ? {
                            serviceType: 'dalles-exterieures',
                            floorCoverFormat,
                            preparatoryWork,
                          }
                        : isPlasterboardService
                        ? {
                            serviceType: 'plaques-de-platre',
                            coveredSurface,
                            finishType,
                          }
                        : isPlasterService
                        ? {
                            serviceType: 'platrage',
                            wallPlasterSurface,
                            ceilingPlasterSurface,
                          }
                        : isParquetService
                        ? {
                            serviceType: 'parquet',
                            bedroomCount,
                            installationType,
                            parquetUnderlay,
                            parquetExistingFloorType,
                          }
                        : isVoletMotoriseService
                        ? {
                            serviceType: 'volet-motorise',
                            voletMotoriseCount,
                            voletMotoriseType,
                            voletMotoriseMaterial,
                            voletMotoriseMasonryWork,
                          }
                        : isObturateurMenuiserieService
                        ? {
                            serviceType: 'obturateur-menuiserie',
                            obturateurCount,
                            obturateurType,
                            obturateurMaterial,
                            obturateurMasonryWork,
                          }
                        : isTerraceService
                        ? {
                            serviceType: 'terrasse',
                            terraceInstallationBase,
                            terracePreparationNeed,
                          }
                        : usesDocumentStepAtStep4
                        ? {
                            serviceType: isMasonryService ? 'maconnerie' : 'revetement-de-sol',
                          }
                        : isPolishedConcreteService
                        ? {
                            serviceType: 'beton-poli',
                            polishedConcreteExistingFloor,
                          }
                        : isCarpetService
                        ? {
                            serviceType: 'tapis',
                            carpetInstallType,
                            carpetRoomEmpty,
                          }
                        : isSkirtingService
                        ? {
                            serviceType: 'plinthes',
                            baseboardType,
                            baseboardFinish,
                            baseboardLength,
                          }
                        : isBornesTableauxElectriquesService
                        ? {
                            serviceType: 'bornes-tableaux-electriques',
                            bornesTableauxRooms,
                          }
                        : isEclairageService
                        ? {
                            serviceType: 'eclairage',
                            eclairageWallType,
                            eclairageElectricalModification,
                          }
                        : isPrisesInterrupteurService
                        ? {
                            serviceType: 'prises-interrupteur',
                            prisesInterrupteurCountRange,
                            prisesInterrupteurReady,
                          }
                        : isInterphoneService
                        ? {
                            serviceType: 'interphone',
                            interphoneSystemType,
                            interphoneInstallType,
                          }
                        : isAlarmesSecuriteService
                        ? {
                            serviceType: 'alarmes-securite',
                            alarmesSecuriteLieu,
                            alarmesSecuriteFonctionnalites,
                          }
                        : isCamerasSecuriteService
                        ? {
                            serviceType: 'cameras-securite',
                            camerasSecuriteNombre,
                          }
                        : isAlarmeCameraService
                        ? {
                            serviceType: 'alarme-camera',
                            alarmeType,
                            alarmeElementCounts,
                          }
                        : isLightingHomeStagingService
                        ? {
                            serviceType: 'eclairage-et-home-staging',
                            lightingObjective,
                            lightingType,
                          }
                        : isCustomFurnitureService
                        ? {
                            serviceType: 'meuble-sur-mesure',
                            customFurnitureMaterials,
                          }
                        : isDecorationConsultingService
                        ? {
                            serviceType: 'conseil-en-decoration',
                            decorationConsultingStyle,
                            decorationConsultingObjective,
                            decorationConsultingSupportLevel,
                          }
                        : isDecorationSpacePlanningService
                        ? {
                            serviceType: 'decoration-et-amenagement-des-espaces',
                            spaceDecorationStyle,
                            spaceDecorationObjective,
                          }
                        : isWallDecorationAccessoriesService
                        ? {
                            serviceType: 'decoration-murale-et-accessoires',
                            wallDecorationElementType,
                            wallDecorationStyle,
                          }
                        : isFurnitureRepairService
                        ? {
                            serviceType: 'reparation-des-meubles',
                            furnitureRepairType,
                            furnitureProblemNature,
                            furnitureRepairMaterial,
                          }
                        : isFurnitureTreatmentService
                        ? {
                            serviceType: 'traitement-des-meubles',
                            furnitureType,
                            furnitureMaterial,
                            furnitureTreatmentType,
                          }
                        : isKitchenFurnitureService
                        ? {
                            serviceType: 'meubles-de-cuisine',
                            kitchenFurnitureCounts,
                            kitchenWallFixRequired,
                          }
                        : isHomeApplianceService
                        ? {
                            serviceType: 'appareils-menagers',
                            applianceElectricalInstall,
                            applianceWaterConnection,
                          }
                        : isKitchenHoodService
                        ? {
                            serviceType: 'hotte-de-cuisine',
                            kitchenHoodType,
                            kitchenHoodDuctNeed,
                          }
                        : isVmcService
                        ? {
                            serviceType: 'vmc',
                            vmcSystemType,
                            vmcSystemLocation,
                          }
                        : isChargementDechargementService
                        ? {
                            serviceType: 'chargement-dechargement',
                            chargementGoodsTypes,
                            chargementAccessDifficulty,
                            chargementTruckDistance,
                          }
                        : isGardeStockageMeublesService
                        ? {
                            serviceType: 'garde-stockage-meubles',
                            gardeStockageApproxVolume,
                            gardeStockageMainGoodsType,
                            gardeStockageAdditionalServices,
                          }
                        : isGardeAccompagnementEnfantsService
                        ? {
                            serviceType: 'garde-accompagnement-enfants',
                            gardeEnfantsDureeSouhaitee,
                            gardeEnfantsFrequence,
                            gardeEnfantsBesoinParticulier,
                          }
                        : isDebarrasMenageNettoyageService
                        ? {
                            serviceType: 'debarras-menage-nettoyage',
                            debarrasMenageLieu,
                            debarrasMenageAcces,
                            debarrasMenageServicesVoulus,
                          }
                        : isDebarrasService
                        ? {
                            serviceType: 'debarras',
                            debarrasTypeLieu,
                            debarrasAcces,
                            debarrasServicesSouhaites,
                          }
                        : isLocationUtilitaireAvecChauffeurService
                        ? {
                            serviceType: 'location-utilitaire-chauffeur',
                            utilitaireChauffeurMode,
                            utilitaireLieuPriseEnCharge,
                            utilitaireVolumeTypeTransport,
                          }
                        : isMenageSurfaceMeublesService
                        ? {
                            serviceType: 'menage-surfaces-et-meubles',
                            menageSurfacesSuperficieApprox,
                            menageSurfacesPrestationType,
                            menageSurfacesProduitsExigences,
                          }
                        : isRepassageService
                        ? {
                            serviceType: 'repassage',
                            repassageFrequence,
                            repassagePrestationsComplementaires,
                          }
                        : isNettoyageVitresService
                        ? {
                            serviceType: 'nettoyage-vitres',
                            nettoyageVitresTypeAcces,
                            nettoyageVitresFrequence,
                          }
                        : isNettoyageTapisCanapesService
                        ? {
                            serviceType: 'nettoyage-tapis-canapes',
                            nettoyageTapisNombreArticles,
                            nettoyageTapisTypePrincipal,
                            nettoyageTapisServicesAdditionnels,
                          }
                        : isOrganisationRangementService
                        ? {
                            serviceType: 'organisation-rangement',
                            organisationRangementNiveau,
                            organisationRangementDuree,
                            organisationRangementServicesAdditionnels,
                          }
                        : isMontageDemontageMeublesService
                        ? {
                            serviceType: 'montage-demontage-meubles',
                            montageDemontageMeublesCountRange,
                          }
                        : isAccompagnementPersonnesAgeesService
                        ? {
                            serviceType: 'accompagnement-personnes-agees',
                            accompagnementAinesLieu,
                            accompagnementAinesAutonomie,
                          }
                        : isShowerCabinService
                        ? {
                            serviceType: 'cabine-de-douche',
                            showerDismantleNeed,
                            showerDismantleElements,
                          }
                        : isBathroomLightingService
                        ? {
                            serviceType: 'eclairage-salle-de-bain',
                            bathroomLightingType,
                            bathroomLightingZone,
                            bathroomLightingObjective,
                          }
                        : isToiletService
                        ? {
                            serviceType: 'toilettes',
                            toiletLocation,
                            toiletWaterSupplyReady,
                            toiletEvacuationType,
                          }
                        : isBathroomStorageService
                        ? {
                            serviceType: 'salle-de-bain-meubles-rangement',
                            bathroomStorageType,
                            bathroomStorageObjective,
                          }
                        : isWaterHeaterService
                        ? {
                            serviceType: 'chauffe-eau',
                            waterHeaterCapacity,
                            waterHeaterElectricalReady,
                            waterHeaterWaterSupplyReady,
                            waterHeaterThermostatInstall,
                          }
                        : isMasticService
                        ? {
                            serviceType: 'mastic',
                            masticColorRequirement,
                          }
                        : isClimatisationService
                        ? {
                            serviceType: 'climatisation',
                            climatisationUnitType,
                            climatisationOutletNearby,
                          }
                        : isChauffageService
                        ? {
                            serviceType: 'chauffage',
                            chauffagePeripheralCounts,
                            chauffageSupplyReady,
                          }
                        : isChaudiereService
                        ? {
                            serviceType: 'chaudiere',
                            chaudiereEnergyType,
                            chaudiereUsage,
                          }
                        : isEntretienDepannagePlumbingService
                        ? {
                            serviceType: 'entretien-depannage-plomberie',
                            entretienDepannageInterventionType,
                            entretienDepannageEquipment,
                            entretienDepannageProblemNature,
                          }
                        : isTuyauxService
                        ? {
                            serviceType: 'tuyaux',
                            tuyauxRooms,
                          }
                        : isKitchenPlumbingService
                        ? {
                            serviceType: 'plomberie-cuisine',
                            kitchenPlumbingInterventionType,
                            kitchenPlumbingEquipment,
                            kitchenPlumbingProblemNature,
                          }
                        : isKitchenFaucetService
                        ? {
                            serviceType: 'robinetterie-cuisine',
                            kitchenFaucetType,
                            kitchenFaucetInterventionType,
                            kitchenFaucetProblemNature,
                          }
                        : isKitchenStorageService
                        ? {
                            serviceType: 'espace-rangement',
                            kitchenStorageType,
                            kitchenStorageObjective,
                          }
                        : isBathroomService
                        ? {
                            serviceType: 'salle-de-bain',
                            bathroomEquipment,
                            bathroomInterventionType,
                          }
                        : isPortailMotoriseService
                        ? {
                            serviceType: 'portail-motorise',
                            portailMotoriseType,
                            portailMotoriseMaterial,
                            portailMotorisePosts,
                          }
                        : isPortailMenuiserieService
                        ? {
                            serviceType: 'portail-menuiserie',
                            portailGateType,
                            portailMaterial,
                            portailPosts,
                          }
                        : isGarageMotoriseService
                        ? {
                            serviceType: 'garage-motorise',
                            porteGarageMaterial,
                            porteGarageSupportWork,
                            porteGarageRemoveOld,
                            porteGarageLocking,
                          }
                        : isPorteGarageMenuiserieService
                        ? {
                            serviceType: 'porte-de-garage-menuiserie',
                            porteGarageMaterial,
                            porteGarageSupportWork,
                            porteGarageRemoveOld,
                            porteGarageLocking,
                          }
                        : isDomotiqueService
                        ? {
                            serviceType: 'domotique',
                            domotiqueDeviceCounts,
                            domotiqueControlMode,
                          }
                        : isPotagerService
                        ? {
                            serviceType: 'potager',
                            potagerPlantCount,
                            potagerSoilReady,
                          }
                        : isFleursService
                        ? {
                            serviceType: 'fleurs',
                            fleursPlantCountRange,
                            fleursSoilReady,
                          }
                        : isFeuillesService
                        ? {
                            serviceType: 'feuilles',
                            feuillesType,
                          }
                        : isArbustesService
                        ? {
                            serviceType: 'arbustes',
                            arbustesSoilReady,
                          }
                        : isClairiereService
                        ? {
                            serviceType: 'clairiere',
                            clairiereTreeHeight,
                            clairiereSoilReady,
                          }
                        : isArbrePlantationService
                        ? {
                            serviceType: 'arbre-plantation',
                            arbrePlantingHeight,
                            arbreSoilReady,
                          }
                        : isAbattageArbresService
                        ? {
                            serviceType: 'abattage-arbres',
                            abattageTreeCount,
                            abattageTreeHeight,
                          }
                        : isPelouseService
                        ? {
                            serviceType: 'pelouse',
                            pelouseSoilAmendments,
                          }
                        : isHaiesService
                        ? {
                            serviceType: 'haies',
                            haiesTools,
                          }
                        : isTondreService
                        ? {
                            serviceType: 'tondre-pelouse',
                            tondreTools,
                            tondreGrassRemoval,
                          }
                        : isEspaceVertsService
                        ? {
                            serviceType: 'espace-verts',
                            espaceVertsTools,
                            espaceVertsRegularMaintenance,
                          }
                        : isOuverturePorteService
                        ? {
                            serviceType: 'ouverture-porte',
                            ouverturePorteTypePorte,
                            ouverturePorteProblemeRencontre,
                          }
                        : isPortesSecuriseesService
                        ? {
                            serviceType: 'portes-securisees',
                            portesSecuriseesTypePorte,
                            portesSecuriseesSerrure,
                          }
                        : isSerrurerieService
                        ? {
                            serviceType: 'serrurerie',
                            serrurerieTypePorteSerrure,
                            serrurerieProbleme,
                          }
                        : isEntretienReparationMecaniqueService
                        ? {
                            serviceType: 'entretien-reparation-mecanique',
                            mecaniqueEntretienPrestationPrincipale,
                          }
                        : isChangementRouesMecaniqueService
                        ? {
                            serviceType: 'changement-roues-mecanique',
                            mecaniqueChangementRouesNombre,
                          }
                        : isBatterieElectriqueMecaniqueService
                        ? {
                            serviceType: 'batterie-electrique-mecanique',
                            mecaniqueBatterieVehicule,
                            mecaniqueBatterieSymptome,
                            mecaniqueBatterieServicesAdditionnels,
                          }
                        : isPlaquetteFreinMecaniqueService
                        ? {
                            serviceType: 'plaquette-frein-mecanique',
                            mecaniquePlaquetteFreinSymptome,
                            mecaniquePlaquetteFreinServicesAdditionnels,
                          }
                        : isDepannageRemorquageMecaniqueService
                        ? {
                            serviceType: 'depannage-remorquage-mecanique',
                            mecaniqueDepannageTypePanne,
                            mecaniqueDepannageUrgence,
                            mecaniqueDepannageDestinationRemorquage,
                          }
                        : isChangementPiecesAutoMecaniqueService
                        ? {
                            serviceType: 'changement-pieces-auto-mecanique',
                            mecaniquePiecesAutoPieceConcernee,
                          }
                        : isDiagnosticNettoyageMecaniqueService
                        ? {
                            serviceType: 'diagnostic-nettoyage-mecanique',
                            mecaniqueDiagnosticNettoyageProblemePrincipal,
                          }
                        : isPorteMenuiserieService
                        ? {
                            serviceType: 'porte-menuiserie',
                            porteDoorType,
                            porteInterventionDetail,
                            porteProblemNature,
                            porteMaterial,
                          }
                        : isFenetresPortesFenetresService
                        ? {
                            serviceType: 'fenetres-portes-fenetres',
                            fenetresFrameMaterial,
                            fenetresWindowType,
                          }
                        : isVestiaireMenuiserieService
                        ? {
                            serviceType: 'vestiaire-menuiserie',
                            vestiaireDoorCounts,
                            vestiaireInteriorCounts,
                          }
                        : isCuisineMenuiserieService
                        ? {
                            serviceType: 'cuisine-menuiserie',
                            menuiserieCuisineArmoiresCounts,
                            menuiserieCuisineLinearMeters,
                            menuiserieCuisineLayout,
                          }
                        : isStoreMotoriseService
                        ? {
                            serviceType: 'store-motorise',
                            storeMotoriseCount,
                            storeMotoriseMaterial,
                            storeMotoriseElectricityReady,
                          }
                        : isStoresMenuiserieService
                        ? {
                            serviceType: 'stores-menuiserie',
                            storesBlindCounts,
                            storesWallType,
                          }
                        : isCurtainRodService
                        ? {
                            serviceType: 'tringle-a-rideaux',
                            curtainRodWallType,
                          }
                        : isFixationMuralService
                        ? {
                            serviceType: 'fixation-murale',
                            fixationWallType,
                          }
                        : isAssemblyService
                        ? {
                            serviceType: 'assemblage',
                            assemblyFurnitureCounts,
                            assemblyFurnitureCondition,
                            assemblyWallFixRequired,
                            assemblyWallType,
                          }
                        : isShelfService
                        ? {
                            serviceType: 'etagere',
                            shelfWallType,
                          }
                        : isWallpaperService
                        ? {
                            serviceType: 'papier-peint',
                            wallLength,
                            wallHeight,
                            wallCoverType,
                          }
                        : isPaintingService
                          ? {
                              serviceType: 'peinture',
                              wallCoverRemoval,
                              paintSurfaceState,
                              protectionAreas,
                            }
                          : {
                              serviceType: 'carrelage',
                              tileFinish,
                              tileSize,
                              surfaceState,
                            },
                      requestStep5Details: usesDocumentStepAtStep4
                        ? {
                            documents: uploadedFiles.map((file) => ({
                              name: file.name,
                              size: file.size,
                              type: file.type,
                            })),
                            selectedImageUrl,
                          }
                        : location.state?.requestStep5Details,
                    },
                  });
                }}
              />
            </div>
          </div>
        </NioSection.Content>
      </NioSection>
    </AppLayout>
  );
}

export default RequestDemandStep4;
